import React, { useState, useEffect, useRef, useCallback } from 'react';
import LayoutWrapper from '@iso/components/utility/layoutWrapper.js';
import PageHeader from '@iso/components/utility/pageHeader';
import IntlMessages from '@iso/components/utility/intlMessages';
import PropertyMapPage from './PropertyMap.styles';
import { Map, TileLayer, FeatureGroup, Polygon, Tooltip, ZoomControl, LayerGroup, Circle } from 'react-leaflet';
import { PlusOutlined, PushpinFilled, SettingOutlined, StarFilled, StarOutlined, DeleteOutlined, SaveOutlined, HomeOutlined } from '@ant-design/icons';
import { useSelector, useDispatch } from 'react-redux';
import { propertyService, observationService, introductionService, hazardService, mapViewService } from '@iso/services';
import { Button, Dropdown, Menu, Checkbox, Radio, Space, Modal, DatePicker, Row, Col, Form, Select, message, Card, Input } from 'antd';
import { EditControl } from "react-leaflet-draw";
import PrimaryObjectModal from './components/PrimaryObject/PrimaryObjectModal';
import _ from 'lodash';
import { objectColors } from '@iso/constants/objectColors';
import { taskService, mobService, primaryObjectService, assetService, layerService } from '@iso/services';
import Task from './components/Task/Task';
import Mob from './components/Mob/Mob';
import Asset from './components/Asset/Asset';
import NewLiveStockModal from './components/LiveStock/NewLiveStockModal';
import BtnSaveGroup from './components/BtnSaveGroup/BtnSaveGroup';
import btnSaveAction from '@iso/redux/btnSave/actions';
import modalActions from '@iso/redux/modal/actions';
import Control from '@skyeer/react-leaflet-custom-control'
import AppLocale from '@iso/config/translation';
import { IntlProvider } from 'react-intl';
import { Provider } from 'react-redux';
import { store } from '@iso/redux/store';
import { DownOutlined } from '@ant-design/icons';
import L from 'leaflet';
import { detect } from 'detect-browser';
import ReactLeafletGoogleLayer from 'react-leaflet-google-layer';
import AreaSummaryModal from '@iso/containers/Pages/PropertyMap/components/PrimaryObject/AreaSummaryModal';
import AddLayerModal from './components/Layer/AddLayerModal';
import DrawTool from './components/Layer/DrawTool';
import { dateHelper } from '@iso/lib/helpers/dateHelper';
import { commonHelper } from '@iso/lib/helpers/commonHelper';
import { markerGridHelper } from '@iso/lib/helpers/markerGridHelper';
import HeatBar from '@iso/components/utility/heatBar';
import { iotDevice } from '@iso/services';
import DeviceMapMarker from '@iso/containers/Pages/SafeFarm/DeviceMapMarker';
import UserLocationMarkers from './components/UserLocation/UserLocationMarkers';
import Observation from './components/Observation/Observation';
import PrimaryObjectFilterModal from '@iso/containers/Pages/PropertyMap/components/PrimaryObject/PrimaryObjectFilterModal';
import AssetFilterModal from '@iso/containers/Pages/PropertyMap/components/Asset/AssetFilterModal';
import { assetCategories } from '@iso/constants/assetCategories';
import { propertyHelper } from "@iso/lib/helpers/propertyHelper";
// jQuery removed — was only used for DOM existence checks, replaced with document.querySelector
import HarvestFilterModal from "./components/Harvest/HarvestFilterModal";
import HarvestModal from './components/Harvest/HarvestModal';
import propertyActions from "@iso/redux/property/actions";
import Hazard from './components/Hazard/Hazard';
import ActivityHazardMarkers from './components/Hazard/ActivityHazardMarkers';
import ModalActivity from '@iso/containers/Pages/HealthSafety/ModalActivity';
import { getHazardCentroid } from '../../../constants/hazardGeometry';
import { mapAnnotationService, annotationRouteService } from '@iso/services';
import AnnotationToolbar from './components/Annotation/AnnotationToolbar';
import AnnotationMarkers from './components/Annotation/AnnotationMarkers';
import MapLegend from './components/Annotation/MapLegend';
import AnnotationRouteMarkers, { ActiveRouteTool, RouteInProgress, ZoneInProgress, SavedZones } from './components/Annotation/RouteTool';
import { getIconDef, ANNOTATION_CATEGORIES } from './components/Annotation/AnnotationIcons';
import AnnotationSidePanel from './components/Annotation/AnnotationSidePanel';
import { DrawingControlBar, AnnotationGlobalStyles } from './components/Annotation/Annotation.styles';
import { ColorSwatches } from './components/Annotation/AnnotationShared';
import { setMapScaleUnit, isImperialAcreageUnit } from '@iso/lib/helpers/mapFurniture';
import { setMeasurementsEnabled, getMeasurementsEnabled } from '@iso/lib/helpers/mapMeasurements';
import { safeRemoveLayer, HAZARD_ZONE_STYLE } from './components/Hazard/hazardUtils';
import inside from 'point-in-polygon';
// turf is lazily loaded in listAllMarkerArea() to reduce initial bundle size
import { Marker, Popup } from 'react-leaflet';
// intro.js is lazily loaded in startIntro() to avoid bundling it on every page load

var geodesy = require('leaflet-geodesy');
const { show } = btnSaveAction;
const { openModal } = modalActions;
const { changeActiveProperty } = propertyActions;
const { Option } = Select;

// ── Utility helpers (DRY) ───────────────────────────────────────────────
/**
 * Extract polygon coordinates from a Leaflet layer.
 * @param {L.Layer} layer - Leaflet polygon/rectangle layer
 * @param {'array'|'string'} format - 'array' returns [[lat,lng],...], 'string' returns ["lat, lng",...]
 */
const extractPolygonCoordinates = (layer, format = 'array') => {
  const points = layer.getLatLngs()[0];
  if (format === 'string') {
    return points.map((point) => `${point.lat}, ${point.lng}`);
  }
  return points.map((point) => Object.values(point));
};

/**
 * Convert hectare acreage to the property's preferred unit.
 */
const ACREAGE_CONVERSIONS = { acres: 2.471, yard: 11959.9005 };
const convertAcreage = (hectares, unit) => {
  const h = parseFloat(hectares);
  const factor = ACREAGE_CONVERSIONS[unit];
  return factor ? (h * factor).toFixed(2) : h;
};

// ── Monkey-patch: L.Draw.Polyline.removeHooks null-safety ──────────────
// Targets: leaflet-draw 1.0.4 (Leaflet/Leaflet.draw#927)
// Polyline.removeHooks (inherited by Polygon) directly
// accesses this._mouseMarker.off(...) and this._map.removeLayer(...) without
// null guards.  SimpleShape (Circle, Rectangle) already guards with
// `if (this._map)`.  This patch adds the same protection so a partially-
// initialised or already-torn-down handler cannot crash on unmount.
if (L.Draw && L.Draw.Polyline && L.Draw.Polyline.prototype.removeHooks) {
  const _origPolylineRemoveHooks = L.Draw.Polyline.prototype.removeHooks;
  L.Draw.Polyline.prototype.removeHooks = function () {
    if (!this._map) return;          // map already gone – nothing to clean up
    try {
      _origPolylineRemoveHooks.call(this);
    } catch (e) {
      // Swallow the error – the handler was in a broken state but the
      // map is being torn down anyway so there is nothing to recover.
    }
  };
}

const PropertyMap = (props) => {
  const activePropertyId = useSelector((state) => state.property.activePropertyId);
  const activeProperty = localStorage.getItem('active_property');
  const activePermission = useSelector((state) => state.permission.activePermission);
  const activePropertySubscription = useSelector((state) => state.subscription.activePropertySubscription);
  const activePropertyTrialSatelliteExpired = useSelector((state) => state.property_trial_satellite.activePropertyTrialSatelliteExpired);
  const mapRef = useRef();
  const [propertyId, setPropertyId] = useState(null);
  const btnVisibility = useSelector((state) => state.btnSave.btnVisibility);
  const saveSuccess = useSelector((state) => state.btnSave.saveSuccess);
  const cancelSave = useSelector((state) => state.btnSave.cancelSave);
  const dataTransfer = useSelector((state) => state.btnSave.data);
  const [position, setPosition] = useState([null, null]);
  const [property, setProperty] = useState();
  const [currentPos, setCurrentPos] = useState({});
  const controlDraw = React.useRef(null);
  const controlDrawMarker = React.useRef(null);
  const hazardRetryTimer = React.useRef(null);
  const objectRef = React.useRef([]);
  const [primaryObjects, setPrimaryObjects] = useState([]);
  const [editableFG, setEditableFG] = useState(null);
  const [currentLayer, setCurrentLayer] = useState(null);
  const [currentObject, setCurrentObject] = useState(null);
  const dispatch = useDispatch();
  const modalData = useSelector((state) => state.modal.modalData);
  const { locale } = useSelector(state => state.LanguageSwitcher.language);
  const currentAppLocale = AppLocale[locale];
  const [visibleShowOption, setVisibleShowOption] = useState(false);
  const [visibleSelectProperty, setVisiblitySelectProperty] = React.useState(false);
  const [dataObject, setDataObject] = useState({});
  const [visibleSummary, setVisibleSummary] = useState(false);
  const [zoomDefault, setZoomDefault] = useState(16);
  const [disabledArea, setDisabledArea] = useState(false);
  const [darkTheme, setDarkTheme] = useState(false);
  const [trackTrace, setTrackTrace] = useState(false);
  const [propertyPIC, setPropertyPIC] = useState("");
  const [propertyAcreageUnit, setPropertyAcreageUnit] = useState("");
  const [layerChosen, setLayerChosen] = useState(0);
  const [listLayer, setListLayer] = useState([]);
  const [isEditingLayer, setIsEditingLayer] = useState(false);
  const [form] = Form.useForm();
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(false);
  const [positionDevice, setPositionDevice] = useState();
  const [showMarkerAlert, setShowMarkerAlert] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();
  const [actionName, setActionName] = useState(null);
  const [selectedPrimaryObjectTypeIds, setSelectedPrimaryObjectTypeIds] = useState();
  const [notSelectedPrimaryObjectTypeIds, setNotSelectedPrimaryObjectTypeIds] = useState([]);
  const [notSelectedBlockObjectTypeIds, setNotSelectedBlockObjectTypeIds] = useState([]);
  const [visiblePrimaryObjectFilterModal, setVisiblePrimaryObjectFilterModal] = useState(false);
  const [visibleAssetFilterModal, setVisibleAssetFilterModal] = useState(false);
  const [visibleBlockFilter, setVisibleBlockFilter] = useState(false);
  const [selectedAssetCategories, setSelectedAssetCategories] = useState([]);
  const [notSelectedAssetCategories, setNotSelectedAssetCategories] = useState([]);
  const [isAddLayerControl, setIsAddLayerControl] = useState(false);
  const [isShowAreaLabel, setIsShowAreaLabel] = useState(true);
  const [isShowAreaSize, setIsShowAreaSize] = useState(false);
  // Seeded from the flag already in force, not from `true`: changeActiveProperty
  // has normally applied the property's answer before this mounts, and starting
  // from the default would turn the labels back on for a property that had them
  // off — until the fetch below corrects it, or permanently if that fetch fails.
  const [isShowEdgeDistance, setIsShowEdgeDistance] = useState(getMeasurementsEnabled);
  const [isShowBlockLabel, setIsShowBlockLabel] = useState(true);
  const [harvest, setHarvest] = useState([]);
  const [selectedBlocks, setSelectedBlocks] = useState([]);
  const [isopenHarvest, setIsopenHarvest] = useState(false)
  const currentUser = useSelector((state) => state.user.user);
  const [showHazard, setShowHazard] = useState(false);
  const [mapViews, setMapViews] = useState([]);
  const [propertyDefaultView, setPropertyDefaultView] = useState(null);
  const [activeMapViewId, setActiveMapViewId] = useState(null);
  const [saveViewModalVisible, setSaveViewModalVisible] = useState(false);
  const [manageViewsModalVisible, setManageViewsModalVisible] = useState(false);
  const [newViewName, setNewViewName] = useState('');
  const introRef = useRef(null);
  const Block_Dummy_Data = [
    {
      id: 11,
      name: "Block",
      data_block: [
        ["18.619147217251626", "73.73946189880373"],
        ["18.607603978284995", "73.72778892517091"],
        ["18.61833433873807", "73.760404586792"],
      ],
      lat_centroid: "18.615028511424896",
      lng_centroid: "73.74255180358888",
      acreage: 364.52,
      property_id: 161,
      color_label: "yellow",
      color: "yellow",
      crop: 75,
      variety: "plant",
      type: "plant",
      start_time: "2024-04-02 10:51:00",
      finish_time: "2024-04-09 10:51:03",
      type_id: 140,
      crop_label: "crop",
      acreage_unit: "Acres",
      acreage_by_acres: 364.52,
      acreage_by_hectare: "147.52",
      acreage_by_yard: "1764324.52",
      primary_object_id: 1468,
    },
    {
      id: 12,
      name: "Block2",
      data_block: [
        ["18.600775214766838", "73.74830245971681"],
        ["18.59475917233299", "73.74332427978517"],
        ["18.595897358825507", "73.7505340576172"],
      ],
      lat_centroid: "18.597143915308447",
      lng_centroid: "73.74738693237306",
      acreage: 54.73,
      color_label: "green",
      color: "green",
      crop: 75,
      variety: "plant",
      type: "field_Prep",
      start_time: "2024-04-02 10:51:00",
      finish_time: "2024-04-09 10:51:03",
      type_id: 140,
      crop_label: "crop",
      acreage_unit: "Acres",
      acreage_by_acres: 54.73,
      acreage_by_hectare: "22.15",
      acreage_by_yard: "264911.80",
    },
  ];
  const [showHazardZones, setShowHazardZones] = useState(false);
  const [hazardZoneData, setHazardZoneData] = useState([]);
  const [activityHazardZoneData, setActivityHazardZoneData] = useState([]);
  const [hazardActivityRefreshKey, setHazardActivityRefreshKey] = useState(0);
  const [hazardActivityModalVisible, setHazardActivityModalVisible] = useState(false);
  const [hazardActivityEditingPolicy, setHazardActivityEditingPolicy] = useState(null);
  const [hazardActivityInitialPoint, setHazardActivityInitialPoint] = useState(null);
  const [annotations, setAnnotations] = useState([]);
  const [annotationRoutes, setAnnotationRoutes] = useState([]);
  const [pendingAnnotationType, setPendingAnnotationType] = useState(null);
  const [activeAnnotationCategory, setActiveAnnotationCategory] = useState(null);
  const [routeWaypoints, setRouteWaypoints] = useState([]);
  const [routeColor, setRouteColor] = useState('#3B82F6');
  const [routeStyle, setRouteStyle] = useState('dashed');
  const [routeLabel, setRouteLabel] = useState('');
  const [zoneColor, setZoneColor] = useState('#16A34A');
  const [zoneStyle, setZoneStyle] = useState('dashed');
  const [zoneLabel, setZoneLabel] = useState('');
  const [hiddenAnnotationCategories, setHiddenAnnotationCategories] = useState([]);
  const [showLegend, setShowLegend] = useState(false);
  const [showAnnotationToolbar, setShowAnnotationToolbar] = useState(true);
  const [drawingPanelOpen, setDrawingPanelOpen] = useState(false);
  const [activeTool, setActiveTool] = useState(null);
  const routeDrawing = activeTool === 'route';
  const [zoneWaypoints, setZoneWaypoints] = useState([]);
  const [textLabelModalVisible, setTextLabelModalVisible] = useState(false);
  const [textLabelPendingPos, setTextLabelPendingPos] = useState(null);
  const [textLabelInput, setTextLabelInput] = useState('');
  const [showHazardShapeSelector, setShowHazardShapeSelector] = useState(false);
  const [hazardShapeType, setHazardShapeType] = useState(null);
  const [hazardTempLayer, setHazardTempLayer] = useState(null);
  const [hazardEditMode, setHazardEditMode] = useState(false);
  const [pendingDrawArea, setPendingDrawArea] = useState(false);
  const [tilesLoaded, setTilesLoaded] = useState(false);
  const propertyCenterPoint = React.useMemo(() => (
    position[0] !== null && position[1] !== null
      ? { lat: position[0], lng: position[1] }
      : null
  ), [position]);

  // Derive a { lat, lng } center from hazard geometry for the ModalActivity map
  const hazardModalCenter = React.useMemo(() => {
    return getHazardCentroid(hazardActivityInitialPoint, propertyCenterPoint);
  }, [hazardActivityInitialPoint, propertyCenterPoint]);

  // Memoize draw/edit option objects so react-leaflet-draw's EditControl
  // does not tear down and rebuild the Leaflet.Draw toolbar on every render.
  // Without this, new object references trigger componentDidUpdate which
  // removes + recreates the toolbar, racing with enableAction() calls.
  const hasDrawPermission = (activePermission === 'owner' || activePermission === 'admin' || activePermission === 'modify');
  const hasFullPermission = (activePermission === 'owner' || activePermission === 'admin');

  const hazardShapeOptions = { color: '#ff4d4f', fillColor: '#ff4d4f', fillOpacity: 0.3, weight: 2 };

  const drawOptionsMain = React.useMemo(() => ({
    marker: false,
    polyline: false,
    circle: hasDrawPermission ? { shapeOptions: hazardShapeOptions } : false,
    circlemarker: false,
    rectangle: false,
    polygon: hasFullPermission
      ? { allowIntersection: false, showArea: true, shapeOptions: hazardShapeOptions }
      : (hasDrawPermission
        ? { allowIntersection: false, showArea: false, shapeOptions: hazardShapeOptions }
        : false),
  }), [hasDrawPermission, hasFullPermission]);

  const editOptionsMain = React.useMemo(() => ({
    remove: hasFullPermission,
    edit: hasDrawPermission,
  }), [hasFullPermission, hasDrawPermission]);

  const drawOptionsMarker = React.useMemo(() => ({
    marker: hasFullPermission,
    polyline: false,
    circle: false,
    circlemarker: false,
    rectangle: false,
    polygon: false,
  }), [hasFullPermission]);

  const editOptionsMarker = React.useMemo(() => ({
    remove: false,
    edit: false,
  }), []);

  useEffect(() => {
    const assetCategoryValues = assetCategories.map((assetCategory) => {
      return assetCategory.value
    })
    setSelectedAssetCategories(assetCategoryValues);
    setHarvest(Block_Dummy_Data);
  }, [])

  useEffect(() => {
    const assetCategoryValues = assetCategories.map((assetCategory) => {
      return assetCategory.value
    })
    const notSelectedCategory = assetCategoryValues.filter((category) => {
      return selectedAssetCategories.indexOf(category) === -1
    })
    setNotSelectedAssetCategories(notSelectedCategory)
  }, [selectedAssetCategories])

  const cancelAssetFilterModal = () => {
    setVisibleAssetFilterModal(false)
  }

  const cancelBlockFilterModal = () => {
    setVisibleBlockFilter(false);
  };

  const updatePropertyDetail = React.useCallback((id) => {
    propertyService.viewProperty(id).then(res => {
      if (res.code === '0000') {
        setPosition([res.property.lat, res.property.lng]);
        setPrimaryObjects(res.primary_objects);
        setPropertyPIC(res.property.PIC);
        setPropertyAcreageUnit(res.property.acreage_unit);
        setIsShowEdgeDistance(Number(res.property.map_show_edge_distance) === 1);
        setProperty(res.property);
      }
    }).catch(err => {
      console.error('Failed to load property details:', err);
    });
  }, []);

  // The scale bar is built with the map, before the property (and its acreage
  // unit) has loaded, so switch its unit system once that arrives.
  useEffect(() => {
    setMapScaleUnit(mapRef.current?.leafletElement, isImperialAcreageUnit(propertyAcreageUnit));
  }, [propertyAcreageUnit]);

  // Same shape as the scale bar above: the maps are built before the property
  // has loaded, so push the property's choice into them once it arrives — and
  // again whenever it is toggled. No remount, so the view and layers survive.
  useEffect(() => {
    setMeasurementsEnabled(isShowEdgeDistance);
  }, [isShowEdgeDistance]);

  // Property-level, so this writes through for everyone rather than living in
  // this browser. Flip the map first for instant feedback, and put it back if
  // the write fails.
  const handleChangeShowEdgeDistance = (value) => {
    setIsShowEdgeDistance(value);

    // `{ success, message }` — this endpoint answers in the newer shape, and
    // its message is always a string, so it can be shown as it arrives.
    propertyService.updateMapSettings(propertyId, value).then(res => {
      if (!res.success) {
        setIsShowEdgeDistance(!value);
        message.error(res.message || 'Could not save the map setting');
      }
    }).catch(err => {
      setIsShowEdgeDistance(!value);
      message.error('Could not save the map setting');
      console.error('Failed to update map settings:', err);
    });
  };

  const openHazardActivityModal = React.useCallback((policy = null, point = null) => {
    setHazardActivityEditingPolicy(policy);
    setHazardActivityInitialPoint(point);
    setHazardActivityModalVisible(true);
  }, []);

  const closeHazardActivityModal = React.useCallback(() => {
    safeRemoveLayer(editableFG, hazardTempLayer);
    setHazardTempLayer(null);
    setHazardEditMode(false);
    setHazardActivityModalVisible(false);
    setHazardActivityEditingPolicy(null);
    setHazardActivityInitialPoint(null);
    setCurrentObject(null);
    setActionName(null);
  }, [hazardTempLayer, editableFG]);

  // Confirm the drawn/edited hazard area and open the hazard form
  const confirmHazardArea = React.useCallback(() => {
    if (!hazardTempLayer) return;

    const layer = hazardTempLayer;
    let hazardGeometry = {};

    if (layer instanceof L.Circle) {
      const center = layer.getLatLng();
      const radius = layer.getRadius();
      hazardGeometry = {
        shape_type: 'circle',
        circle_center: { lat: center.lat, lng: center.lng },
        circle_radius: radius,
      };
    } else if (layer instanceof L.Marker) {
      const point = layer.getLatLng();
      hazardGeometry = { lat: point.lat, lng: point.lng };
    } else if (layer instanceof L.Rectangle) {
      const points = layer.getLatLngs();
      const arrPointsTmp = [];
      for (const point of points[0]) {
        arrPointsTmp.push(`${point.lat}, ${point.lng}`);
      }
      hazardGeometry = {
        shape_type: 'rectangle',
        data_area: arrPointsTmp,
      };
    } else {
      // Polygon
      const points = layer.getLatLngs();
      const arrPointsTmp = [];
      for (const point of points[0]) {
        arrPointsTmp.push(`${point.lat}, ${point.lng}`);
      }
      hazardGeometry = {
        shape_type: 'polygon',
        data_area: arrPointsTmp,
      };
    }

    // Disable editing handles
    if (layer.editing) layer.editing.disable();
    if (layer.dragging) layer.dragging.disable();

    // Open the hazard form with final geometry
    setHazardEditMode(false);
    setHazardActivityEditingPolicy(null);
    setHazardActivityInitialPoint(hazardGeometry);
    setHazardActivityModalVisible(true);
    setActionName(null);
    setHazardShapeType(null);
  }, [hazardTempLayer]);

  // Cancel editing the hazard area and clean up
  const cancelHazardEdit = React.useCallback(() => {
    if (hazardTempLayer) {
      if (hazardTempLayer.editing) hazardTempLayer.editing.disable();
      if (hazardTempLayer.dragging) hazardTempLayer.dragging.disable();
      safeRemoveLayer(editableFG, hazardTempLayer);
    }
    setHazardTempLayer(null);
    setHazardEditMode(false);
    setHazardShapeType(null);
    setActionName(null);
  }, [hazardTempLayer, editableFG]);

  // Handle "Edit Area" / re-draw request from ModalActivity
  const handleRequestDrawArea = React.useCallback(() => {
    closeHazardActivityModal();
    // Small delay to let modal close, then open shape selector
    setTimeout(() => {
      if (tilesLoaded) {
        setShowHazardShapeSelector(true);
      } else {
        setPendingDrawArea(true);
      }
    }, 300);
  }, [closeHazardActivityModal, tilesLoaded]);

  // Check sessionStorage for pending draw area request from HealthSafety page navigation
  useEffect(() => {
    if (sessionStorage.getItem('pendingDrawArea') === 'true') {
      sessionStorage.removeItem('pendingDrawArea');
      setPendingDrawArea(true);
    }
  }, []);

  // Safely disable any active draw handlers before the component unmounts
  // so Leaflet.Draw's cleanup doesn't crash accessing a destroyed map.
  useEffect(() => {
    return () => {
      const disableToolbar = (ref) => {
        try {
          const el = ref.current?.leafletElement;
          if (!el?._toolbars) return;
          Object.values(el._toolbars).forEach(tb => {
            if (tb.enabled && tb.enabled()) {
              try { tb.disable(); } catch (e) { /* already torn down */ }
            }
          });
        } catch (e) { /* ref unavailable */ }
      };
      disableToolbar(controlDraw);
      disableToolbar(controlDrawMarker);
      // Cancel any pending hazard draw retry timer
      if (hazardRetryTimer.current) clearTimeout(hazardRetryTimer.current);
    };
  }, []);

  // Open shape selector only after map tiles, FeatureGroup AND draw tools are fully ready
  useEffect(() => {
    if (!pendingDrawArea || !editableFG || !tilesLoaded) return;

    // editableFG (FeatureGroup) is set, but EditControl children may not
    // have fully initialised their Leaflet.Draw toolbars yet.  Poll the
    // ref so we don't open the selector too early.
    const checkReady = () => {
      if (controlDraw.current?.leafletElement?._toolbars?.draw) {
        setPendingDrawArea(false);
        setShowHazardShapeSelector(true);
        return true;
      }
      return false;
    };

    if (!checkReady()) {
      const interval = setInterval(() => {
        if (checkReady()) {
          clearInterval(interval);
        }
      }, 100);
      return () => clearInterval(interval);
    }
  }, [pendingDrawArea, editableFG, tilesLoaded]);

  const refreshHazardActivities = React.useCallback(() => {
    setHazardActivityRefreshKey((key) => key + 1);
    if (updatePropertyDetail && propertyId) {
      updatePropertyDetail(propertyId);
    }
  }, [updatePropertyDetail, propertyId]);

  // getProperties() call removed — PrivateLayout already handles property
  // restoration and auto-select, including showing SelectPropertyModal when
  // no properties exist.

  useEffect(() => {
    if (propertyId && isEditingLayer && layerChosen) {
      setAnnotations([]);
      setAnnotationRoutes([]);
      mapAnnotationService.getList(propertyId, layerChosen).then(res => {
        if (res && res.annotations) setAnnotations(res.annotations);
      }).catch(err => console.error('Failed to load annotations:', err));
      annotationRouteService.getList(propertyId, layerChosen).then(res => {
        if (res && res.routes) setAnnotationRoutes(res.routes);
      }).catch(err => console.error('Failed to load annotation routes:', err));
    } else if (!isEditingLayer) {
      setAnnotations([]);
      setAnnotationRoutes([]);
    }
  }, [propertyId, isEditingLayer, layerChosen]);

  const saveArea = React.useCallback((dataTransfer) => {
    var layer = objectRef.current[dataTransfer.dataId].leafletElement;
    var arrPoints = extractPolygonCoordinates(layer, 'array');
    var acreage = (geodesy.area(layer) / 10000).toFixed(2);

    var dataTmp = {
      primary_object: {
        data_area: _.map(arrPoints, (point) => {
          return point.slice(0, 2).toString().replace(',', ', ');
        }),
        acreage
      }
    }

    primaryObjectService.updateArea(dataTmp, dataTransfer.dataId).then(res => {
      if (res.code === '0000') {
        layer.editing.disable();
      }
    }).catch(err => {
      console.error('Failed to update area:', err);
    });
  }, []);

  const enableDrawing = React.useCallback((dataTransfer) => {
    if (currentObject) {
      if (currentObject === 'addArea') {
        enableAction('draw', 'polygon');
      } else if (currentObject === 'addTask') {
        enableAction('draw', 'marker');
      } else if (currentObject === 'addMob') {
        enableAction('draw', 'marker');
      } else if (currentObject === 'addAsset') {
        enableAction('draw', 'marker');
      } else if (currentObject === 'addObservation') {
        enableAction('draw', 'marker');
      } else if (currentObject === 'addHazard') {
        if (hazardEditMode) return;
        if (tilesLoaded) {
          setShowHazardShapeSelector(true);
        } else {
          setPendingDrawArea(true);
        }
      }
    }
  });

  useEffect(() => {
    if (props.isShowBlock) {
      L.drawLocal.draw.toolbar.buttons.polygon = 'Create Block';
    } else {
      L.drawLocal.draw.toolbar.buttons.polygon = 'Create Object (Paddock)';
    }
    L.drawLocal.edit.toolbar.buttons.edit = 'Edit Object (Paddock)';
    L.drawLocal.edit.toolbar.buttons.remove = 'Delete Item';
    L.drawLocal.draw.toolbar.buttons.marker = 'Create Task';
  }, [props.data]);


  useEffect(() => {
    if (currentObject) {
      enableDrawing(currentObject);
    }
  }, [currentObject]);

  const cancelSaveArea = React.useCallback((propertyId, dataObjectId) => {
    var layer = objectRef.current[dataObjectId].leafletElement;
    // updatePropertyDetail(propertyId);
    layer.editing.disable();
  }, []);

  useEffect(() => {
    if (activePropertyId) {
      setPropertyId(activePropertyId);
      updatePropertyDetail(activePropertyId);
    }
  }, [activePropertyId, updatePropertyDetail]);

  useEffect(() => {
    if (saveSuccess && dataTransfer.type === 'area') {
      saveArea(dataTransfer);
    } else if (cancelSave && dataTransfer.type === 'area') {
      cancelSaveArea(propertyId, dataTransfer.dataId);
    }
  }, [saveSuccess, saveArea, cancelSave, cancelSaveArea, propertyId, dataTransfer])

  const onEdited = (editedObject) => {
    const objectTmp = _.map(primaryObjects, (object) => {
      if (object.id === editedObject.id) {
        return editedObject;
      }
      return object;
    })
    setPrimaryObjects(objectTmp)
  }

  const menu = () => (
    <Menu onClick={handleMenuClick} style={{ width: '150px' }}>
      <Menu.Item key="addArea">
        <IntlMessages id="propertyMapPage.area" />
      </Menu.Item>
      <Menu.Item key="addTask">
        <IntlMessages id="propertyMapPage.task" />
      </Menu.Item>
      <Menu.Item key="addMob">
        <IntlMessages id="propertyMapPage.mob" />
      </Menu.Item>
      <Menu.Item key="addLiveStock">
        <IntlMessages id="propertyMapPage.livestock" />
      </Menu.Item>
      <Menu.Item key="addAsset">
        <IntlMessages id="propertyMapPage.asset" />
      </Menu.Item>
      {
        propertyHelper.checkIsGoldPackage(activePropertySubscription) &&
        <Menu.Item key="addObservation">
          <IntlMessages id="propertyMapPage.observation" />
        </Menu.Item>
      }
      {
        propertyHelper.checkIsGoldPackage(activePropertySubscription) &&
        <Menu.Item key="addHazard">
          <IntlMessages id="propertyMapPage.hazard" />
        </Menu.Item>
      }
    </Menu>
  );

  const enableAction = (actionType, value) => {
    if (controlDraw.current.leafletElement._toolbars[actionType]._modes[value]) {
      controlDraw.current.leafletElement._toolbars[actionType]._modes[value].handler.enable();
    } else {
      controlDrawMarker.current.leafletElement._toolbars[actionType]._modes[value].handler.enable();
    }
  }

  const handleHazardShapeSelected = (shapeType) => {
    if (hazardEditMode) return;
    setHazardShapeType(shapeType);
    setShowHazardShapeSelector(false);
    setActionName('addHazard');

    // Activate the draw tool directly.  With memoised draw/edit option
    // objects and stable keys the Leaflet.Draw toolbar is never torn-down
    // between renders, so we can call enableAction right away instead of
    // deferring through a useEffect (which races with componentDidUpdate).
    const drawShape = shapeType === 'marker' ? 'marker' : shapeType;
    let attempts = 0;
    // Clear any pending retry from a previous selection
    if (hazardRetryTimer.current) clearTimeout(hazardRetryTimer.current);
    const tryEnable = () => {
      hazardRetryTimer.current = null;
      try {
        enableAction('draw', drawShape);
      } catch (error) {
        attempts++;
        if (attempts < 20) {
          hazardRetryTimer.current = setTimeout(tryEnable, 150);
        } else {
          message.error('Failed to activate drawing tool. Please try again.');
        }
      }
    };
    tryEnable();
  }

  const handleMenuClick = async (e) => {
    setCurrentObject(e.key);

    if (e.key === 'addArea') {
      await setDisabledArea(true);
      enableAction('draw', 'polygon');
    } else if (e.key === 'addTask') {
      setActionName('addTask')
      enableAction('draw', 'marker');
    } else if (e.key === 'addObservation') {
      updatePropertyDetail(activePropertyId);
      setActionName('addObservation')
      enableAction('draw', 'marker');
    } else if (e.key === 'addHazard') {
      if (hazardEditMode) return;
      if (tilesLoaded) {
        setShowHazardShapeSelector(true);
      } else {
        setPendingDrawArea(true);
      }
    }
    else if (e.key === 'addMob') {
      updatePropertyDetail(activePropertyId);
      var layer = null;
      var type = 'mob';
      var point = null;
      var canCreate = false;
      const modalData = {
        featureGroup: editableFG,
        layer,
        type,
        canCreate,
        object: {
          point
        }
      }
      dispatch(openModal(modalData));
    } else if (e.key == "addLiveStock") {
      var type = 'livestock';
      const modalData = {
        type
      }
      dispatch(openModal(modalData));
    } else if (e.key == 'addAsset') {
      updatePropertyDetail(activePropertyId);
      setActionName('addAsset');
      enableAction('draw', 'marker');
    }

  }

  const onCreated = async drawControl => {
    var type = drawControl.layerType,
      layer = drawControl.layer;
    setCurrentLayer(layer);

    // Handle hazard area drawing (polygon/circle) — enter edit mode so user can adjust before confirming
    if (actionName === 'addHazard' && (type === 'polygon' || type === 'rectangle' || type === 'circle')) {
      // Remove any previous temp layer before storing the new one
      safeRemoveLayer(editableFG, hazardTempLayer);

      let editableLayer = layer;

      if (type === 'polygon' || type === 'rectangle') {
        // Enable vertex editing so user can drag each point
        editableLayer.editing.enable();
      } else if (type === 'circle') {
        // Enable circle editing so user can drag edge to resize
        editableLayer.editing.enable();
      }

      // Store layer and enter edit mode — user must click "Confirm Area" to proceed
      setHazardTempLayer(editableLayer);
      setHazardEditMode(true);
      return;
    }

    if (type === 'polygon') {
      handlePolygon(layer);
    } else if (type === 'marker') {
      handleMarker(layer);
    }
    await setDisabledArea(false);
  }

  const handleMarker = (layer) => {
    let type = "";
    let canCreate = true;
    switch (actionName) {
      case ("addObservation"):
        type = "observation";
        break;
      case ("addHazard"):
        type = "hazard";
        break;
      case ("addTask"):
        type = "task";
        break;
      case ("addAsset"):
        type = "asset";
        break;
      default:
        type = "task";
    }

    var point = layer.getLatLng();
    if (type === 'hazard') {
      // Remove any previous temp layer
      safeRemoveLayer(editableFG, hazardTempLayer);
      // Keep marker on map and make it draggable so user can adjust position
      layer.dragging.enable();
      setHazardTempLayer(layer);
      setHazardEditMode(true);
      return;
    }

    const modalData = {
      featureGroup: editableFG,
      layer,
      type,
      canCreate,
      object: {
        point
      }
    }
    dispatch(openModal(modalData));
  }

  const handlePolygon = (layer) => {
    var arrPoints = extractPolygonCoordinates(layer, 'array');
    var acreage = convertAcreage((geodesy.area(layer) / 10000).toFixed(2), propertyAcreageUnit);

    const modalData = {
      featureGroup: editableFG,
      layer,
      type: 'area',
      object: { coordinates: arrPoints, acreage }
    }
    dispatch(openModal(modalData));
  }

  const onFeatureGroupReady = reactFGref => {
    // store the featureGroup ref for future access to content
    setEditableFG(reactFGref);
  };
  const handleShowBlock = (objects, heatMap) => {
    var html = [];
    if (objects.length > 0) {
      _.forEach(objects, function (object) {
        const colorIndex = _.findIndex(objectColors, (color) => {
          return color.value === object.color;
        });
        var color = objectColors[colorIndex]
          ? objectColors[colorIndex].color
          : "rgb(49, 166, 197)";
        html.push(
          <Polygon
            key={1}
            positions={object.data_block}
            onClick={handleClickBlock.bind(this, object)}
            fillColor={color}
            color={showHeatMap ? "#000000" : color}
            className={`block-objects type_id_${object.id}`}
            ref={(el) => (objectRef.current[object.id] = el)}
            fillOpacity={showHeatMap ? 1 : 0.2}
          >
            <Tooltip
              permanent={true}
              direction="center"
              className={`block-objects-tooltip ${showHeatMap ? "dark" : ""
                } type_id_${object.id} ${!isShowBlockLabel ? "not-show-block-label" : ""
                }`}
            >
              <p>{object.name}</p>
              {isShowAreaSize && <p>
                {parseFloat(object.acreage).toFixed(1)}
                <span> {object.acreage_unit}</span>
              </p>}
            </Tooltip>
          </Polygon>
        );
      });
    }
    return html;
  };
  const handleShowPrimaryObjects = (objects, heatMap) => {


    var html = [];
    if (objects.length > 0) {
      _.forEach(objects, function (object) {
        if (heatMap) {
          if (object.total_mobs == 0) {
            var color = commonHelper.generateRestColor(object.rest_days)
          } else {
            var color = commonHelper.generateColorInUse(object.in_use_days)
          }
        } else {
          const colorIndex = _.findIndex(objectColors, (color) => {
            return color.value === object.color
          });
          var color = objectColors[colorIndex] ? objectColors[colorIndex].color : 'rgb(49, 166, 197)';
        }
        html.push(
          <Polygon key={object.id}
            positions={object.area}
            onClick={handleClickObject.bind(this, object)}
            onRemove={handleRemoveObject.bind(this, object)}
            onEdit={handleEditObject.bind(this, object)}
            fillColor={color}
            color={showHeatMap ? '#000000' : color}
            className={`primary-objects type_id_${object.type_id}`}
            ref={el => (objectRef.current[object.id] = el)}
            fillOpacity={showHeatMap ? 1 : 0.2}
          >
            <Tooltip permanent={true} direction="center" className={`primary-objects-tooltip ${showHeatMap ? 'dark' : ''} type_id_${object.type_id} ${!isShowAreaLabel ? 'not-show-area-label' : ''}`}>
              <p>{object.name}</p>
              {isShowAreaSize && <p>{parseFloat(object.acreage).toFixed(1)}<span> {object.acreage_unit === 'Hectare' ? 'Ha' : object.acreage_unit}</span></p>}
            </Tooltip>
          </Polygon>
        );
      });
    }
    return html;
  }

  const handleClickBlock = (object, e) => {
    if (
      !controlDraw.current.leafletElement._toolbars.edit._modes.remove ||
      !controlDraw.current.leafletElement._toolbars.edit._modes.remove.handler
        ._enabled
    ) {
      showEditBlockModal(object);
      setCurrentPos(e.latLng)
    }
  };
  const showEditBlockModal = (object) => {
    const modalData = {
      object: object,
      type: "block",
      // hideEditBlock: true,
    };
    setIsopenHarvest(true)
    dispatch(openModal(modalData));
  };
  const handleClickObject = (object, e) => {
    if (disabledArea == true) {
      return false;
    }
    if (!controlDraw.current.leafletElement._toolbars.edit._modes.remove || !controlDraw.current.leafletElement._toolbars.edit._modes.remove.handler._enabled) {
      if (!btnVisibility) {
        // const modalData = {
        //   object,
        //   type: 'summary',
        //   hideEditArea: !(activePermission == "owner" || activePermission == "admin" || activePermission == "modify")
        // }
        // dispatch(openModal(modalData));
        setDataObject({});
        viewPrimaryObject(object.id);
        setCurrentPos(e.latlng);
        showSummaryModal();
      }
    }
  }
  const viewPrimaryObject = (id) => {
    primaryObjectService.view(id).then(res => {
      if (res.code === '0000') {
        setDataObject(res.primary_object);
      }
    }).catch(err => {
      console.error('Failed to view primary object:', err);
    });
  }
  const showSummaryModal = () => {
    setVisibleSummary(true);
  }

  const cancelSummaryModal = () => {
    setVisibleSummary(false);
  }

  const [deletedObjs, setDeletedObjs] = useState([]);
  const handleRemoveObject = (object, e) => {
    setDeletedObjs((prev) => [...prev, object.id]);
  }

  const [updatedObjs, setUpdatedObjs] = useState({});
  const handleEditObject = (object, e) => {
    let LatLngs = e.target.getLatLngs()[0];

    let area = _.map(LatLngs, (point) => [point.lat, point.lng]);
    let area2 = _.map(LatLngs, (point) => `${point.lat}, ${point.lng}`);

    object.area = area;
    setUpdatedObjs((prev) => ({ ...prev, [object.id]: area2 }));
  }

  const editArea = () => {
    const data = {
      objectRef,
      dataId: modalData.object.id,
      type: 'area'
    }
    dispatch(show(data));
    objectRef.current[modalData.object.id].leafletElement.editing.enable();
  }

  const onMapDeleted = async (e) => {
    try {
      const destroyPromises = [];

      _.forEach(deletedMobs, function (mobId) {
        destroyPromises.push(mobService.destroy(mobId));
      });
      setDeletedMobs([]);

      _.forEach(deletedTasks, function (taskId) {
        destroyPromises.push(taskService.destroy(taskId));
      });
      setDeletedTasks([]);

      _.forEach(deletedObjs, function (objectId) {
        destroyPromises.push(primaryObjectService.destroy(objectId));
      });
      setDeletedObjs([]);

      _.forEach(deletedAssets, function (assetId) {
        destroyPromises.push(assetService.destroy(assetId));
      });
      setDeletedAssets([]);

      _.forEach(deletedObservations, function (observationId) {
        destroyPromises.push(observationService.destroy(observationId));
      });
      setDeletedObservations([]);

      if (deletedHazards.length > 0) {
        destroyPromises.push(
          hazardService.bulkDestroy(deletedHazards)
        );
        setDeletedHazards([]);
      }

      await Promise.allSettled(destroyPromises);
      updatePropertyDetail(activePropertyId);
    } catch (error) {
      console.error('Failed to delete map items:', error);
    }
  }

  // ── Deleted-item handlers (consolidated) ──────────────────────────────
  const [deletedMobs, setDeletedMobs] = useState([]);
  const handleSetDeletedMobs = (mobId) => setDeletedMobs((prev) => [...prev, mobId]);

  const [deletedTasks, setDeletedTasks] = useState([]);
  const handleSetDeletedTasks = (taskId) => setDeletedTasks((prev) => [...prev, taskId]);

  const [deletedAssets, setDeletedAssets] = useState([]);
  const handleSetDeletedAssets = (assetId) => setDeletedAssets((prev) => [...prev, assetId]);

  const [deletedObservations, setDeletedObservations] = useState([]);
  const handleSetDeletedObservations = (observationsId) => setDeletedObservations((prev) => [...prev, observationsId]);

  const [deletedHazards, setDeletedHazards] = useState([]);
  const handleSetDeletedHazards = (hazardId) => setDeletedHazards((prev) => [...prev, hazardId]);

  const messageWarning = () => {
    messageApi.open({
      type: 'warning',
      content: 'The mob area is not enough',
    });
  };

  const getRandomLatLng = (coordinates) => {
    var polygon = L.polygon([
      coordinates
    ]);
    var bounds = polygon.getBounds();
    var x_max = bounds.getEast();
    var x_min = bounds.getWest();
    var y_max = bounds.getSouth();
    var y_min = bounds.getNorth();
    var lat = 0, lng = 0;
    var count = 0;
    var area = polygon.getLatLngs()[0].map(function (point) {
      return [point.lat, point.lng]
    });

    while (!inside([lat, lng], area)) {
      lat = y_min + (Math.random() * (y_max - y_min));
      lng = x_min + (Math.random() * (x_max - x_min));
      count += 1;
      if (count == 100) {
        break;
      }
    }

    return new L.LatLng(
      lat,
      lng
    );
  }

  const onMapEdited = (e) => {

    _.forEach(updatedTasks, function (taskData, taskId) {
      const data = {
        task: taskData
      }
      taskService.storeOrUpdateTasks(data, taskId);
    });

    setUpdatedTasks({});

    // _.forEach(updatedMobs, function(mobData, mobId){
    //   const data = {
    //     mob: mobData
    //   }
    //   mobService.storeOrUpdate(data, mobId);
    // });
    // updatePropertyDetail(activePropertyId);
    // setUpdatedMobs({});

    _.forEach(updatedObjs, function (dataArea, objectId) {
      const data = {
        primary_object: {
          data_area: dataArea
        }
      }
      primaryObjectService.updateArea(data, objectId)
    });

    setUpdatedObjs({});

    _.forEach(updatedAssets, function (facilityData, assetId) {
      const data = {
        facility: facilityData
      }
      assetService.storeOrUpdate(data, assetId)
    });
    setUpdatedAssets({});

    _.forEach(updatedObservations, function (observationData, observationId) {
      const data = {
        observation: observationData
      }
      observationService.storeOrUpdateObservation(data, observationId);
    });

    setUpdatedObservations({});

    _.forEach(updatedHazards, function (hazardData, hazardId) {
      const data = {
        hazard: hazardData
      }
      hazardService.storeOrUpdateHazard(data, hazardId);
    });

    setUpdatedHazards({});
  }




  // ── Updated-item handlers (consolidated, immutable state) ─────────────
  const [updatedTasks, setUpdatedTasks] = useState({});
  const handleSetUpdatedTasks = (task, LatLng) => {
    setUpdatedTasks((prev) => ({ ...prev, [task.id]: { ...task, point: LatLng } }));
  }

  const [updatedMobs, setUpdatedMobs] = useState({});
  const handleSetUpdatedMobs = (mob, LatLng) => {
    setUpdatedMobs((prev) => ({ ...prev, [mob.id]: { ...mob, point: LatLng } }));
  }

  const [updatedAssets, setUpdatedAssets] = useState({});
  const handleSetUpdatedAssets = (asset, LatLng) => {
    setUpdatedAssets((prev) => ({ ...prev, [asset.id]: { ...asset, point: LatLng } }));
  }

  const [updatedObservations, setUpdatedObservations] = useState({});
  const handleSetUpdatedObservations = (observation, LatLng) => {
    setUpdatedObservations((prev) => ({ ...prev, [observation.id]: { ...observation, point: LatLng } }));
  }

  const [updatedHazards, setUpdatedHazards] = useState({});
  const handleSetUpdatedHazards = (hazard, LatLng) => {
    const shapeType = hazard.shape_type || 'marker';
    let entry;
    if (shapeType === 'marker') {
      entry = { ...hazard, point: LatLng };
    } else if (shapeType === 'polygon' || shapeType === 'rectangle') {
      entry = { ...hazard, shape_type: shapeType, data_area: LatLng };
    } else if (shapeType === 'circle') {
      entry = { ...hazard, shape_type: 'circle', circle_center: LatLng.center, circle_radius: LatLng.radius };
    }
    if (entry) {
      setUpdatedHazards((prev) => ({ ...prev, [hazard.id]: entry }));
    }
  }

  const [showArea, setShowArea] = useState(true);
  const [showAsset, setShowAsset] = useState(false);
  const [showTask, setShowTask] = useState(false);
  const [showMob, setShowMob] = useState(false);
  const [showObservation, setShowObservation] = useState(false);
  const [showSatellite, setShowSatellite] = useState(true);
  const [showHeatMap, setShowHeatMap] = useState(false);
  const [showBlock, setShowBlock] = useState(false);
  // Everyone on the property at their last known position, from the shared
  // location contract: members, plus the visitor and contractor check-in
  // sessions of people who have no account. Defaults OFF — it polls.
  const [showUserLocations, setShowUserLocations] = useState(false);
  const handleTrackTraceChange = useCallback((checked) => {
    setTrackTrace(checked);
    if (checked) {
      getPositionDevice();
    } else {
      updatePropertyDetail(propertyId);
    }
  }, [propertyId]);
  const menuShowOptions = useCallback(() => (
    <Menu>
      <Menu.Item key="showArea" onClick={() => setShowArea(v => !v)}>
        <span onClick={(e) => e.stopPropagation()}>
          <Checkbox checked={showArea} onChange={() => setShowArea(v => !v)} id='show-area'>
            <IntlMessages id="propertyMapPage.showArea" />
          </Checkbox>
        </span>
        <SettingOutlined onClick={(e) => { e.stopPropagation(); setVisiblePrimaryObjectFilterModal(true); }} />
      </Menu.Item>
      <Menu.Item key="showBlock" onClick={() => setShowBlock(v => !v)}>
        <span onClick={(e) => e.stopPropagation()}>
          <Checkbox checked={showBlock} onChange={() => setShowBlock(v => !v)} id="show-block">
            <IntlMessages id="Block" />
          </Checkbox>
        </span>
        <SettingOutlined onClick={(e) => { e.stopPropagation(); setVisibleBlockFilter(true); }} />
      </Menu.Item>
      <Menu.Item key="showAsset" onClick={() => setShowAsset(v => !v)}>
        <span onClick={(e) => e.stopPropagation()}>
          <Checkbox checked={showAsset} onChange={() => setShowAsset(v => !v)} id='show-asset'>
            <IntlMessages id="propertyMapPage.showAsset" />
          </Checkbox>
        </span>
        <SettingOutlined onClick={(e) => { e.stopPropagation(); setVisibleAssetFilterModal(true); }} />
      </Menu.Item>
      <Menu.Item key="showTask" onClick={() => setShowTask(v => !v)}>
        <span onClick={(e) => e.stopPropagation()}>
          <Checkbox checked={showTask} onChange={() => setShowTask(v => !v)} id='show-task'>
            <IntlMessages id="propertyMapPage.showTask" />
          </Checkbox>
        </span>
      </Menu.Item>
      <Menu.Item key="showMob" onClick={() => setShowMob(v => !v)}>
        <span onClick={(e) => e.stopPropagation()}>
          <Checkbox checked={showMob} onChange={() => setShowMob(v => !v)} id='show-mob'>
            <IntlMessages id="propertyMapPage.showMob" />
          </Checkbox>
        </span>
      </Menu.Item>
      <Menu.Item key="showHazard" onClick={() => setShowHazard(v => !v)}>
        <span onClick={(e) => e.stopPropagation()}>
          <Checkbox checked={showHazard} onChange={() => setShowHazard(v => !v)} id='show-hazard'>
            <IntlMessages id="propertyMapPage.showHazard" />
          </Checkbox>
        </span>
      </Menu.Item>
      <Menu.Item key="showHazardZones" onClick={() => setShowHazardZones(v => !v)}>
        <span onClick={(e) => e.stopPropagation()}>
          <Checkbox checked={showHazardZones} onChange={() => setShowHazardZones(v => !v)} id='show-hazard-zones'>
            <IntlMessages id="propertyMapPage.showHazardZones" />
          </Checkbox>
        </span>
      </Menu.Item>
      {propertyHelper.checkIsGoldPackage(activePropertySubscription) &&
        <Menu.Item key="showHeatMap" onClick={() => setShowHeatMap(v => !v)}>
          <span onClick={(e) => e.stopPropagation()}>
            <Checkbox checked={showHeatMap} onChange={() => setShowHeatMap(v => !v)} id='show-heat-map'>
              <IntlMessages id="propertyMapPage.showHeatMap" />
            </Checkbox>
          </span>
        </Menu.Item>
      }
      {(propertyHelper.checkIsSilverPackage(activePropertySubscription) || propertyHelper.checkIsGoldPackage(activePropertySubscription)
        || ((activePropertySubscription === process.env.REACT_APP_STRIPE_PLAN_BRONZE || activePropertySubscription === false) && activePropertyTrialSatelliteExpired === false)) ?
        <Menu.Item key="showSatellite" onClick={() => setShowSatellite(v => !v)}>
          <span onClick={(e) => e.stopPropagation()}>
            <Checkbox checked={showSatellite} onChange={() => setShowSatellite(v => !v)} id='show-satellite'>
              <IntlMessages id="propertyMapPage.showSatellite" />
            </Checkbox>
          </span>
        </Menu.Item> : ''
      }
      {!showSatellite && <Menu.Item key="darkTheme" onClick={() => setDarkTheme(v => !v)}>
        <span onClick={(e) => e.stopPropagation()}>
          <Checkbox checked={darkTheme} onChange={() => setDarkTheme(v => !v)} id='dark-theme'>
            <IntlMessages id="propertyMapPage.darkTheme" />
          </Checkbox>
        </span>
      </Menu.Item>
      }
      {propertyHelper.checkIsGoldPackage(activePropertySubscription) &&
        <Menu.Item key="trackTrace" onClick={() => handleTrackTraceChange(!trackTrace)}>
          <span onClick={(e) => e.stopPropagation()}>
            <Checkbox checked={trackTrace} onChange={(e) => handleTrackTraceChange(e.target.checked)} id='track-trace'>
              <IntlMessages id="propertyMapPage.trackTrace" />
            </Checkbox>
          </span>
        </Menu.Item>
      }
      <Menu.Item key="userLocations" onClick={() => setShowUserLocations(v => !v)}>
        <span onClick={(e) => e.stopPropagation()}>
          <Checkbox checked={showUserLocations} onChange={(e) => setShowUserLocations(e.target.checked)} id='user-locations'>
            <IntlMessages id="propertyMapPage.userLocations" />
          </Checkbox>
        </span>
      </Menu.Item>
      <Menu.Divider />
      {propertyDefaultView && (
        <Menu.ItemGroup title="Property Default">
          <Menu.Item key={`view-prop-default`} onClick={() => handleLoadView(propertyDefaultView)}>
            <HomeOutlined style={{ color: '#2d6a4f', fontSize: 12, marginRight: 6 }} />
            <span>{propertyDefaultView.name}</span>
          </Menu.Item>
        </Menu.ItemGroup>
      )}
      <Menu.ItemGroup title="My Views">
        {mapViews.map(view => (
          <Menu.Item key={`view-${view.id}`} onClick={() => handleLoadView(view)}>
            <span style={{ marginRight: 8 }}>{view.name}</span>
            {view.is_user_default === 1 && <StarFilled style={{ color: '#d4a017', fontSize: 12 }} />}
          </Menu.Item>
        ))}
        {mapViews.length === 0 && (
          <Menu.Item key="no-views" disabled>No saved views</Menu.Item>
        )}
      </Menu.ItemGroup>
      <Menu.Divider />
      <Menu.Item key="saveView" disabled={mapViews.length >= 5} onClick={() => { setVisibleShowOption(false); setSaveViewModalVisible(true); }}>
        <SaveOutlined style={{ marginRight: 8 }} />
        {mapViews.length >= 5 ? 'Max views reached (5)' : 'Save Current View...'}
      </Menu.Item>
      <Menu.Item key="manageViews" onClick={() => { setVisibleShowOption(false); setManageViewsModalVisible(true); }}>
        <SettingOutlined style={{ marginRight: 8 }} />Manage Views...
      </Menu.Item>
    </Menu>
  ), [showArea, showBlock, showAsset, showTask, showMob, showHazard, showHazardZones,
    showHeatMap, showSatellite, darkTheme, trackTrace, showUserLocations,
    activePropertySubscription,
    activePropertyTrialSatelliteExpired, propertyDefaultView, mapViews, handleTrackTraceChange]);
  const menuLayerOptions = () => {
    let options = [];
    _.forEach(listLayer, (object, index) => {
      options.push(
        <Menu.Item className="menu-item-custom" key={"layer-" + index}>
          <Radio value={object.id}>{object.layer_name}</Radio>
        </Menu.Item>
      );
    })
    return (
      <Radio.Group onChange={onchangeLayer} value={layerChosen}>
        <Menu>
          {options}
          {(
            (propertyHelper.checkIsSilverPackage(activePropertySubscription) && listLayer.length < 1)
            ||
            propertyHelper.checkIsGoldPackage(activePropertySubscription))

            &&
            <Menu.Item className="menu-item-custom" key="addLayer" htmlFor="addLayer" onClick={handleAddLayer} style={{ textAlign: "center" }}>
              <PlusOutlined />
            </Menu.Item>
          }
        </Menu>
      </Radio.Group>
    )
  }

  const onchangeLayer = (e) => {
    setLayerChosen(e.target.value);
    setIsEditingLayer(true);
    setAnnotations([]);
    setAnnotationRoutes([]);
    setActiveTool(null);
    setRouteWaypoints([]);
    setZoneWaypoints([]);
  }
  const handleAddLayer = async () => {
    var type = 'addLayer';
    const modalData = {
      type
    }
    dispatch(openModal(modalData));
  }

  const handleVisibleChange = (flag) => {
    setVisibleShowOption(flag);
  }

  // --- Map View helpers ---
  const collectCurrentViewConfig = () => ({
    showArea, showBlock, showAsset, showTask, showMob,
    showObservation, showHazard, showHazardZones,
    showSatellite, showHeatMap,
    selectedPrimaryObjectTypeIds: selectedPrimaryObjectTypeIds || [],
    notSelectedPrimaryObjectTypeIds: notSelectedPrimaryObjectTypeIds || [],
    selectedAssetCategories: selectedAssetCategories || [],
    notSelectedAssetCategories: notSelectedAssetCategories || [],
    selectedBlocks: selectedBlocks || [],
    notSelectedBlockObjectTypeIds: notSelectedBlockObjectTypeIds || [],
    isShowAreaLabel, isShowAreaSize, isShowBlockLabel,
  });

  const applyViewConfig = (config) => {
    if (!config) return;
    if (config.showArea !== undefined) setShowArea(config.showArea);
    if (config.showBlock !== undefined) setShowBlock(config.showBlock);
    if (config.showAsset !== undefined) setShowAsset(config.showAsset);
    if (config.showTask !== undefined) setShowTask(config.showTask);
    if (config.showMob !== undefined) setShowMob(config.showMob);
    if (config.showObservation !== undefined) setShowObservation(config.showObservation);
    if (config.showHazard !== undefined) setShowHazard(config.showHazard);
    if (config.showHazardZones !== undefined) setShowHazardZones(config.showHazardZones);
    if (config.showSatellite !== undefined) setShowSatellite(config.showSatellite);
    if (config.showHeatMap !== undefined) setShowHeatMap(config.showHeatMap);
    if (config.selectedPrimaryObjectTypeIds !== undefined) setSelectedPrimaryObjectTypeIds(config.selectedPrimaryObjectTypeIds);
    if (config.notSelectedPrimaryObjectTypeIds !== undefined) setNotSelectedPrimaryObjectTypeIds(config.notSelectedPrimaryObjectTypeIds);
    if (config.selectedAssetCategories !== undefined) setSelectedAssetCategories(config.selectedAssetCategories);
    if (config.notSelectedAssetCategories !== undefined) setNotSelectedAssetCategories(config.notSelectedAssetCategories);
    if (config.selectedBlocks !== undefined) setSelectedBlocks(config.selectedBlocks);
    if (config.notSelectedBlockObjectTypeIds !== undefined) setNotSelectedBlockObjectTypeIds(config.notSelectedBlockObjectTypeIds);
    if (config.isShowAreaLabel !== undefined) setIsShowAreaLabel(config.isShowAreaLabel);
    if (config.isShowAreaSize !== undefined) setIsShowAreaSize(config.isShowAreaSize);
    if (config.isShowBlockLabel !== undefined) setIsShowBlockLabel(config.isShowBlockLabel);
  };

  const loadMapViews = async () => {
    if (!propertyId) return null;
    try {
      const res = await mapViewService.getList({ property_id: propertyId });
      setMapViews(res.views || []);
      setPropertyDefaultView(res.property_default || null);
      return res;
    } catch (e) {
      setMapViews([]);
      setPropertyDefaultView(null);
      return null;
    }
  };

  // Load map views and apply default on property change
  useEffect(() => {
    if (!propertyId) return;
    const loadAndApplyDefault = async () => {
      const res = await loadMapViews();
      if (!res) return;
      const views = res.views || [];
      const userDefault = views.find(v => v.is_user_default === 1);
      if (userDefault) {
        applyViewConfig(userDefault.config);
        setActiveMapViewId(userDefault.id);
        return;
      }
      const propDefault = res.property_default;
      if (propDefault) {
        applyViewConfig(propDefault.config);
        setActiveMapViewId(propDefault.id);
      }
    };
    loadAndApplyDefault();
  }, [propertyId]);

  const handleSaveView = async () => {
    if (!newViewName.trim()) {
      message.error('Please enter a view name');
      return;
    }
    try {
      const config = collectCurrentViewConfig();
      await mapViewService.create({
        property_id: propertyId,
        name: newViewName.trim(),
        config,
      });
      message.success('View saved successfully');
      setSaveViewModalVisible(false);
      setNewViewName('');
      loadMapViews();
    } catch (e) {
      message.error(e.message || 'Failed to save view');
    }
  };

  const handleLoadView = (view) => {
    applyViewConfig(view.config);
    setActiveMapViewId(view.id);
    setVisibleShowOption(false);
  };

  const handleDeleteView = (viewId) => {
    Modal.confirm({
      title: 'Delete View',
      content: 'Are you sure you want to delete this view?',
      onOk: async () => {
        try {
          await mapViewService.destroy(viewId);
          message.success('View deleted');
          if (activeMapViewId === viewId) setActiveMapViewId(null);
          loadMapViews();
        } catch (e) {
          message.error(e.message || 'Failed to delete view');
        }
      },
    });
  };

  const handleSetUserDefault = async (viewId) => {
    try {
      await mapViewService.setUserDefault(viewId);
      message.success('Default view updated');
      loadMapViews();
    } catch (e) {
      message.error(e.message || 'Failed to set default view');
    }
  };

  const handleSetPropertyDefault = async (viewId) => {
    try {
      await mapViewService.setPropertyDefault(viewId);
      message.success('Property default view set');
      loadMapViews();
    } catch (e) {
      message.error(e.message || 'Failed to set property default');
    }
  };

  const handleDeletePropertyDefault = () => {
    Modal.confirm({
      title: 'Remove Property Default',
      content: 'Are you sure you want to remove the property default view? All users will fall back to the system default.',
      onOk: async () => {
        try {
          await mapViewService.deletePropertyDefault(propertyId);
          message.success('Property default view removed');
          loadMapViews();
        } catch (e) {
          message.error(e.message || 'Failed to remove property default');
        }
      },
    });
  };

  const cancelSelectPropertyModal = () => {
    setVisiblitySelectProperty(false);
  }

  const browser = detect();

  const changeZoomLevelToDefault = () => {
    setZoomDefault(16);
  }
  const getListLayer = () => {
    layerService.getList(propertyId)
      .then(res => {
        if (res.code === "0000") {
          setListLayer(res.layers);
        }
      })
      .catch(err => {
        console.error('Failed to load layers:', err);
      });
  }
  // Fit map bounds when primaryObjects change
  useEffect(() => {
    const { current = {} } = mapRef;
    const { leafletElement: map } = current;

    if (primaryObjects) {
      var boudary = [];
      _.forEach(primaryObjects, (object, index) => {
        boudary.push([object.lat_centroid, object.lng_centroid]);
      })

      if (primaryObjects.length == 1) {
        setPosition(boudary[0]);
      } else {
        if (boudary.length != 0) {
          map.fitBounds(boudary, { padding: [0, 0] });
        }
      }
    }
  }, [mapRef, primaryObjects]);

  // Load layers once when property changes (separate from primaryObjects to avoid re-fetching)
  useEffect(() => {
    if (propertyId) {
      getListLayer();
    }
  }, [propertyId]);

  useEffect(() => {
    // Add layer control
    if (propertyHelper.checkIsGoldPackage(activePropertySubscription)) {
      const { current = {} } = mapRef;
      if (current) {
        const { leafletElement: map } = current;
        const jawgAttribute = {
          attribution: '<a href="http://jawg.io" title="Tiles Courtesy of Jawg Maps" target="_blank">&copy; <b>Jawg</b>Maps</a> &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
          minZoom: 0,
          maxZoom: 22,
          accessToken: process.env.REACT_APP_JAWG_TOKEN
        }
        var jawgLight = L.tileLayer('https://{s}.tile.jawg.io/jawg-light/{z}/{x}/{y}{r}.png?access-token={accessToken}', jawgAttribute);

        var jawgDark = L.tileLayer('https://{s}.tile.jawg.io/jawg-dark/{z}/{x}/{y}{r}.png?access-token={accessToken}', jawgAttribute);

        var jawgTerrain = L.tileLayer('https://{s}.tile.jawg.io/jawg-terrain/{z}/{x}/{y}{r}.png?access-token={accessToken}', jawgAttribute);

        var esriWorldImagery = L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', {
          attribution: 'Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community'
        });

        // The previous khms1.googleapis.com URL carried a {v} tile-version
        // placeholder that was never supplied, so L.Util.template threw
        // "No value provided for variable {v}" and the layer never rendered.
        // This endpoint needs no version parameter.
        var googleSat = L.tileLayer('https://mt1.google.com/vt/lyrs=s&x={x}&y={y}&z={z}', {
          maxZoom: 20,
          attribution: '&copy; Google',
        });

        var baseMaps = {
          'Light': jawgLight,
          'Dark': jawgDark,
          'Terrain': jawgTerrain,
          'Esri World Imagery': esriWorldImagery,
          'Satellite': googleSat
        };
        if (!isAddLayerControl) {
          L.control.layers(baseMaps).addTo(map);
          setIsAddLayerControl(true)
        }
      }
    }

  }, [mapRef, activePropertySubscription])


  const getPositionDevice = () => {
    const { current = {} } = mapRef;
    const { leafletElement: map } = current;
    let locations = [];
    iotDevice.getDeviceLocation(activePropertyId).then((res) => {
      if (res.code === "0000") {
        setPositionDevice(res.locations);
        setLoading(false);

        if (res.locations && res.locations.length > 0) {
          _.forEach(res.locations, (data) => {
            if (data.latitude && data.longitude) {
              locations.push([data.latitude, data.longitude]);
            }
          });
          if (locations.length) {
            let bounds = new L.LatLngBounds(locations);
            let center = bounds.getCenter();
            setPosition([center.lat, center.lng]);
            if (locations.length > 1) {
              map.fitBounds(bounds, { padding: [30, 30] });
            } else {
              map.setView(locations[0], 16);
            }
          }
        }
      }
    }).catch(err => {
      console.error('Failed to get device locations:', err);
      setLoading(false);
    });
  }


  const listAllMarkerArea = async (object) => {
    const turf = await import('@turf/turf');
    const polygon = L.polygon([
      object.area
    ]);
    const bounds = polygon.getBounds();

    var y_min = polygon.getBounds().getWest();
    var y_max = polygon.getBounds().getEast();
    var x_min = polygon.getBounds().getSouth();
    var x_max = polygon.getBounds().getNorth();
    const cellSide = markerGridHelper.cellSideForBounds(bounds);
    const options = { units: 'kilometers' };
    const extent = [y_min, x_min, y_max, x_max];
    const grid = turf.pointGrid(extent, cellSide, options);

    let positionMarker = [];
    var area = polygon.getLatLngs()[0].map(function (point) {
      return [point.lat, point.lng]
    });
    _.forEach(grid.features, function (point) {
      if (
        inside([point?.geometry?.coordinates[1], point?.geometry?.coordinates[0]], area)
        &&
        point?.geometry?.coordinates[1] < object.lat_centroid
      ) {
        positionMarker.push(
          {
            'lat': point?.geometry?.coordinates[1].toFixed(8),
            'lng': point?.geometry?.coordinates[0].toFixed(8)
          }
        );
      }
    });

    // horizontally sort : lat
    positionMarker.sort(function (a, b) {
      if (a.lat < b.lat) return -1;
      if (a.lat > b.lat) return 1;
      if (a.lat == b.lat) {
        if (a.lat > b.lat) return -1; else return 1;
      }
    });

    // reverse array
    if (x_min > 0 && x_max > 0) {
      positionMarker = [...positionMarker].reverse();
    }

    // no grid point landed inside the paddock south of its centroid
    if (!positionMarker.length) {
      return [];
    }

    // arrange middle in array
    let xHorizontal = positionMarker.filter(x => x.lat == positionMarker[0].lat);

    let xMiddle = xHorizontal[Math.round((xHorizontal.length - 1) / 2)];
    let yAllMiddle = positionMarker.filter(y => y.lng == xMiddle.lng);
    let YMiddle = yAllMiddle[Math.round((yAllMiddle.length - 1) / 2)];
    let xUnderName = positionMarker.filter(x => x.lat == YMiddle.lat);

    let xMarkerSort = [];
    _.forEach(xUnderName, function (pM, i) {
      // get middle item
      xMiddle = xUnderName[Math.round((xUnderName.length - 1) / 2)];

      // get index middle item
      var indexMiddle = _.findIndex(xUnderName, xMiddle);
      if (!_.find(xMarkerSort, xMiddle)) {
        xMarkerSort.push(xMiddle);
        xUnderName.splice(indexMiddle, 1);
      }
    });

    return xMarkerSort;
  }

  const getLatLngMarkerArea = async (number, objectPrimary, arrayData) => {
    const points = [];
    const coordinates = await listAllMarkerArea(objectPrimary);
    if (arrayData.length > 0) {
      _.forEach(coordinates, function (point) {
        if (
          !markerGridHelper.isPositionTaken(point, arrayData) &&
          points.length < number
        ) {
          points.push(point);
        }
      });
    } else if (coordinates.length) {
      points.push(coordinates[0]);
    }
    return points;
  }

  const handleFilterPrimaryObjectType = (type) => {
    setSelectedPrimaryObjectTypeIds(type.selectedTypeIds)
    setNotSelectedPrimaryObjectTypeIds(type.notSelectedTypeIds)
  }
  const handleFilterBlock = (block) => {
    setSelectedBlocks(block.selectedTypeIds);
    setNotSelectedBlockObjectTypeIds(block.notSelectedTypeIds);
  };

  const cancelPrimaryObjectFilterModal = () => {
    setVisiblePrimaryObjectFilterModal(false)
  }

  useEffect(() => {
    const { current = {} } = mapRef;
    if (current) {
      const { leafletElement: map } = current;
      map.on('load', function () {
        setTimeout(startIntro, 2000)

      })
    }
  }, [mapRef]);

  const startIntro = async () => {
    if (!localStorage.getItem('isShowIntroduction') || parseInt(localStorage.getItem('isShowIntroduction')) === 0) {
      return
    }
    const introSteps = [
      {
        element: '#view-selection-dropdown',
        intro: '<img src="/images/introductions/property-map/select-viewed-objects.png" /><p class="introduction-content">You can choose what you want to see here</p>',
      },
      {
        element: '.leaflet-draw-draw-polygon',
        intro: '<img src="/images/introductions/property-map/create-area.png" /><p class="introduction-content">Click on this icon to draw a paddock</p>',
      },
      {
        element: '.leaflet-draw-edit-edit',
        intro: '<img src="/images/introductions/property-map/edit-object.png" /><p class="introduction-content">Click on this icon to edit an object</p>',
      },
      {
        element: '.leaflet-draw-edit-remove',
        intro: '<img src="/images/introductions/property-map/remove-object.png" /><p class="introduction-content">Click on this icon to remove an object</p>',
      },
      {
        element: '.leaflet-control-layers-toggle',
        intro: '<img src="/images/introductions/property-map/select-layer.png" /><p class="introduction-content">See more layers here</p>',
      },
      {
        element: '#layer-dropdown',
        intro: '<img src="/images/introductions/property-map/add-layer.png" /><p class="introduction-content">Layers can be created here</p>',
      },
      {
        element: '#action-dropdown-button',
        intro: '<img src="/images/introductions/property-map/add-object.png" /><p class="introduction-content">You can also add new objects here</p>',
      },
    ]
    let validSteps = [];
    _.forEach(introSteps, function (step) {
      if (document.querySelector(step.element)) {
        validSteps.push(step);
      }
    });

    // Lazy load intro.js only when actually needed
    const [{ default: introJs }] = await Promise.all([
      import('intro.js'),
      import('intro.js/introjs.css'),
    ]);

    const intro = introJs();
    intro.setOptions({
      tooltipClass: 'custom-tour-guild-tooltip',
      steps: validSteps,
    });
    intro.onbeforeexit(markIntroduced);

    intro.start();

    // Save the intro instance to the ref
    introRef.current = intro;

    // Clean up on unmount
    return () => {
      markIntroduced()
      introRef.current.exit();
    };
  }

  useEffect(() => {
    localStorage.setItem('isShowIntroduction', 0);
    introductionService.checkIsIntroduced(currentUser.id, window.location.pathname).then(res => {
      if (res.code === '0000' && !res.is_introduced) {
        localStorage.setItem('isShowIntroduction', 1);
      }
    })
  }, [currentUser.id])

  const markIntroduced = () => {
    introductionService.markIntroduced(currentUser.id, window.location.pathname).then(res => {
      if (res.code === '0000') {
        localStorage.setItem('isShowIntroduction', 0);
      }
    })
  }


  useEffect(() => {
    const { current = {} } = mapRef;
    if (current) {
      const { leafletElement: map } = current;
      // Track when map tiles finish loading so we can safely enable drawing tools
      const onTilesLoaded = () => setTilesLoaded(true);
      map.on('load', onTilesLoaded);
      map.eachLayer((layer) => {
        if (layer._url) {
          layer.on('load', onTilesLoaded);
        }
      });
      // If map is already loaded (e.g. returning to the page), mark ready immediately
      if (map._loaded) {
        setTilesLoaded(true);
      }

      return () => {
        map.off('load', onTilesLoaded);
        map.eachLayer((layer) => {
          if (layer._url) {
            layer.off('load', onTilesLoaded);
          }
        });
      };
    }
  }, [mapRef]);

  const handlePolygonCreation = async (drawControl) => {
    var layer = drawControl.layer;
    var arrPoints = extractPolygonCoordinates(layer, 'array');
    var acreage = convertAcreage((geodesy.area(layer) / 10000).toFixed(2), propertyAcreageUnit);

    props.setBlockAcreage(acreage);
    props?.setBlockCoordinates(arrPoints);
  };

  const handleMapClick = (e) => {
    if (pendingAnnotationType) {
      const { lat, lng } = e.latlng;
      const def = getIconDef(pendingAnnotationType);
      mapAnnotationService.storeOrUpdate({
        map_annotation: {
          annotation_type: pendingAnnotationType,
          category: def ? def.category : 'traffic',
          lat,
          lng,
          property_id: propertyId,
          layer_id: layerChosen || null,
        }
      }).then(res => {
        if (res && res.annotation) setAnnotations(prev => [...prev, res.annotation]);
      }).catch(err => console.error('Failed to save annotation:', err));
      setPendingAnnotationType(null);
      setActiveAnnotationCategory(null);
    } else if (routeDrawing) {
      setRouteWaypoints(prev => [...prev, { lat: e.latlng.lat, lng: e.latlng.lng }]);
    } else if (activeTool === 'zone') {
      setZoneWaypoints(prev => [...prev, { lat: e.latlng.lat, lng: e.latlng.lng }]);
    } else if (activeTool === 'text') {
      setTextLabelPendingPos({ lat: e.latlng.lat, lng: e.latlng.lng });
      setTextLabelModalVisible(true);
    }
  };

  const handleFinishRoute = () => {
    if (routeWaypoints.length < 2) return;
    annotationRouteService.storeOrUpdate({
      annotation_route: {
        property_id: propertyId,
        layer_id: layerChosen || null,
        waypoints: routeWaypoints,
        color: routeColor,
        style: routeStyle,
        label: routeLabel,
        type: 'route',
      }
    }).then(res => {
      if (res && res.route) setAnnotationRoutes(prev => [...prev, res.route]);
    }).catch(err => console.error('Failed to save route:', err));
    setRouteWaypoints([]);
    setRouteLabel('');
    setActiveTool(null);
  };

  const handleCancelRoute = () => {
    setRouteWaypoints([]);
    setActiveTool(null);
  };

  const handleFinishZone = () => {
    if (zoneWaypoints.length < 3) return;
    annotationRouteService.storeOrUpdate({
      annotation_route: {
        property_id: propertyId,
        layer_id: layerChosen || null,
        type: 'zone',
        waypoints: zoneWaypoints,
        color: zoneColor,
        style: zoneStyle,
        label: zoneLabel,
      }
    }).then(res => {
      if (res && res.route) setAnnotationRoutes(prev => [...prev, res.route]);
    }).catch(err => console.error('Failed to save zone:', err));
    setZoneWaypoints([]);
    setZoneLabel('');
    setActiveTool(null);
  };

  const handleCancelZone = () => {
    setZoneWaypoints([]);
    setActiveTool(null);
  };

  const handleTextLabelConfirm = () => {
    if (textLabelPendingPos && textLabelInput.trim()) {
      mapAnnotationService.storeOrUpdate({
        map_annotation: {
          annotation_type: 'text_label',
          category: 'text',
          lat: textLabelPendingPos.lat,
          lng: textLabelPendingPos.lng,
          label: textLabelInput.trim(),
          property_id: propertyId,
          layer_id: layerChosen || null,
        }
      }).then(res => {
        if (res && res.annotation) setAnnotations(prev => [...prev, res.annotation]);
      }).catch(err => console.error('Failed to save text label:', err));
    }
    setTextLabelModalVisible(false);
    setTextLabelPendingPos(null);
    setTextLabelInput('');
  };

  const handleTextLabelCancel = () => {
    setTextLabelModalVisible(false);
    setTextLabelPendingPos(null);
    setTextLabelInput('');
  };

  const handleAnnotationDelete = (id) => {
    setAnnotations(prev => prev.filter(a => a.id !== id));
  };

  const handleAnnotationUpdate = (updated) => {
    setAnnotations(prev => prev.map(a => a.id === updated.id ? updated : a));
  };

  const handleRouteDelete = (id) => {
    setAnnotationRoutes(prev => prev.filter(r => r.id !== id));
  };

  const handleRouteUpdate = (updated) => {
    setAnnotationRoutes(prev => prev.map(r => r.id === updated.id ? updated : r));
  };

  const handleToggleAnnotationCategory = (key) => {
    setHiddenAnnotationCategories(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  };

  const handleSelectDrawingTool = (tool) => {
    if (tool === 'route') {
      setActiveTool(prev => prev === 'route' ? null : 'route');
      setDrawingPanelOpen(false);
      setPendingAnnotationType(null);
      setZoneWaypoints([]);
    } else {
      setActiveTool(prev => prev === tool ? null : tool);
      setDrawingPanelOpen(false);
      setPendingAnnotationType(null);
      setRouteWaypoints([]);
      setZoneWaypoints([]);
    }
  };

  return (
    <>
      <AnnotationGlobalStyles />
      <LayoutWrapper className={`map-wrapper ${browser.name}`} style={{ paddingBottom: '0' }}>
        <PropertyMapPage
          hidden_primary_type_ids={notSelectedPrimaryObjectTypeIds}
          hidden_block_type_ids={notSelectedBlockObjectTypeIds}
          hidden_asset_categories={notSelectedAssetCategories}
          className={`${showArea ? "" : "not-show-area"}  ${showBlock ? "" : "not-show-block"
            } ${showTask ? "" : "not-show-task"} ${showObservation ? "" : "not-show-observation"
            } ${showMob ? "" : "not-show-mob"} ${showAsset ? "" : "not-show-asset"
            } ${showHeatMap ? "leaflet-tile-dark" : ""}`}
        >
          {!isEditingLayer
            ?
            <div style={{ height: "100%", width: '100%', position: 'relative' }}>
              <Map ref={mapRef} center={position[0] != null ? position : [-25.3444, 131.0369]} zoom={zoomDefault} zoomControl={false}>
                {(
                  propertyHelper.checkIsShowSatMap(property, activePropertySubscription)
                  && showSatellite
                ) ?
                  <ReactLeafletGoogleLayer type={'hybrid'} useGoogMapsLoader={false} />
                  :
                  <TileLayer
                    url={darkTheme ? "https://tiles.stadiamaps.com/tiles/alidade_smooth_dark/{z}/{x}/{y}{r}.png" : "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"}
                    attribution="&copy; <a href=&quot;http://osm.org/copyright&quot;>OpenStreetMap</a> contributors"
                  />
                }
                <ZoomControl position="bottomleft" zoomInText="+" zoomOutText="-" />
                <FeatureGroup
                  ref={featureGroupRef => {
                    onFeatureGroupReady(featureGroupRef);
                  }}
                >

                  {/* Start section layer */}
                  <Control position="topleft">
                    <div id="view-selection-dropdown" className="leftComponent">
                      <IntlProvider
                        locale={currentAppLocale.locale}
                        messages={currentAppLocale.messages}
                      >
                        <Dropdown overlay={menuShowOptions} trigger={['click']} open={visibleShowOption} onOpenChange={handleVisibleChange}>
                          <Button className="action-button">
                            <IntlMessages id="propertyMap.title.view" /> <DownOutlined />
                          </Button>
                        </Dropdown>

                      </IntlProvider>
                    </div>
                  </Control>

                  {/* Start section layer */}
                  {(propertyHelper.checkIsGoldPackage(activePropertySubscription) ||
                    propertyHelper.checkIsSilverPackage(activePropertySubscription))
                    &&



                    <Control position="topright">
                      <div id="layer-dropdown" className="rightComponent">
                        <IntlProvider
                          locale={currentAppLocale.locale}
                          messages={currentAppLocale.messages}
                        >
                          <Dropdown overlay={menuLayerOptions} trigger={['click']} >
                            <Button className="action-button">
                              Layer <DownOutlined />
                            </Button>
                          </Dropdown>

                        </IntlProvider>
                      </div>
                    </Control>
                  }
                  {showHeatMap &&
                    <Control position="topright">
                      <div className="rightComponent">
                        <IntlProvider
                          locale={currentAppLocale.locale}
                          messages={currentAppLocale.messages}
                        >
                          <HeatBar />
                        </IntlProvider>
                      </div>
                    </Control>
                  }
                  {/* End section layer */}
                  {(activePermission == "owner" || activePermission == "admin" &&
                    (activePropertySubscription !== process.env.REACT_APP_STRIPE_PLAN_GOLD || activePropertySubscription !== process.env.REACT_APP_STRIPE_PLAN_GOLD_ANNUAL)) &&
                    <Control position="bottomright">
                      <Provider store={store}>
                        <IntlProvider
                          locale={currentAppLocale.locale}
                          messages={currentAppLocale.messages}
                        >
                          <div id="action-dropdown-button" className="leftComponent">
                            <Dropdown overlay={menu()} trigger={['click']}>
                              <Button icon={<PlusOutlined />} type="primary" className="btn-success property-map-add-btn">
                                <IntlMessages id="propertyMapPage.add" />
                              </Button>
                            </Dropdown>

                            {
                              btnVisibility &&
                              <BtnSaveGroup />
                            }
                          </div>

                        </IntlProvider>
                      </Provider>
                    </Control>
                  }
                  <EditControl
                    position='topleft'
                    onCreated={props?.isShowBlock ? handlePolygonCreation : onCreated}
                    onEdited={onMapEdited}
                    onDeleted={onMapDeleted}
                    draw={drawOptionsMain}
                    edit={editOptionsMain}
                    ref={controlDraw}
                    key={`edit-control-draw-${activePermission}`}
                  />
                  <EditControl
                    position='topleft'
                    draw={drawOptionsMarker}
                    edit={editOptionsMarker}
                    ref={controlDrawMarker}
                    key={`edit-control-marker-${activePermission}`}
                  />
                  {hazardEditMode && (
                    <Control position="bottomright">
                      <div style={{
                        display: 'flex',
                        gap: '8px',
                        padding: '10px',
                        background: 'rgba(255, 255, 255, 0.95)',
                        borderRadius: '8px',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                      }}>
                        <Button
                          onClick={cancelHazardEdit}
                          style={{
                            borderColor: '#0B2A1B',
                            color: '#0B2A1B',
                          }}
                        >
                          Cancel
                        </Button>
                        <Button
                          type="primary"
                          onClick={confirmHazardArea}
                          style={{
                            backgroundColor: '#0B2A1B',
                            borderColor: '#0B2A1B',
                          }}
                        >
                          Confirm Area
                        </Button>
                      </div>
                    </Control>
                  )}
                  {handleShowPrimaryObjects(primaryObjects, showHeatMap)}
                  {showBlock && handleShowBlock(harvest, showHeatMap)}
                  <Task
                    propertyId={propertyId}
                    currentLayer={currentLayer}
                    primaryObjects={primaryObjects}
                    controlDraw={controlDraw}
                    updatePropertyDetail={updatePropertyDetail}
                    handleSetDeletedTasks={handleSetDeletedTasks}
                    handleSetUpdatedTasks={handleSetUpdatedTasks}
                    dataObject={dataObject}
                  />

                  <Observation
                    propertyId={propertyId}
                    primaryObjects={primaryObjects}
                    currentLayer={currentLayer}
                    dataObject={dataObject}
                    controlDraw={controlDraw}
                    updatePropertyDetail={updatePropertyDetail}
                    handleSetDeletedObservations={handleSetDeletedObservations}
                    handleSetUpdatedObservations={handleSetUpdatedObservations}
                    activePermission={activePermission}
                    cancelSaveArea={cancelSaveArea}
                  />

                  <Mob
                    propertyId={propertyId}
                    currentLayer={currentLayer}
                    controlDraw={controlDraw}
                    handleSetDeletedMobs={handleSetDeletedMobs}
                    primaryObjects={primaryObjects}
                    updatePropertyDetail={updatePropertyDetail}
                    handleSetUpdatedMobs={handleSetUpdatedMobs}
                    getLatLngMarkerArea={getLatLngMarkerArea}
                  />
                  {isopenHarvest &&
                    <HarvestModal
                      propertyId={propertyId}
                      updatePropertyDetail={updatePropertyDetail}
                      primaryObjects={primaryObjects}
                      currentPos={currentPos}
                      propertyAcreageUnit={propertyAcreageUnit}
                    />}

                  <Asset
                    propertyId={propertyId}
                    controlDraw={controlDraw}
                    currentPos={currentPos}
                    handleSetDeletedAssets={handleSetDeletedAssets}
                    primaryObjects={primaryObjects}
                    updatePropertyDetail={updatePropertyDetail}
                    handleSetUpdatedAssets={handleSetUpdatedAssets}
                    listAllMarkerArea={listAllMarkerArea}
                    property={property}
                  />

                  {showHazard && <>
                    <Hazard
                      propertyId={propertyId}
                      primaryObjects={primaryObjects}
                      dataObject={dataObject}
                      controlDraw={controlDraw}
                      updatePropertyDetail={updatePropertyDetail}
                      handleSetDeletedHazards={handleSetDeletedHazards}
                      handleSetUpdatedHazards={handleSetUpdatedHazards}
                      propertyCenter={propertyCenterPoint}
                      onOpenActivityModal={openHazardActivityModal}
                      reloadKey={hazardActivityRefreshKey}
                      onHazardsLoaded={setHazardZoneData}
                    />
                    <ActivityHazardMarkers
                      propertyId={propertyId}
                      reloadKey={hazardActivityRefreshKey}
                      onOpenActivityModal={openHazardActivityModal}
                      onHazardsLoaded={setActivityHazardZoneData}
                    />
                  </>}
                  {trackTrace &&
                    <DeviceMapMarker
                      propertyId={propertyId}
                      getPositionDevice={getPositionDevice}
                      setLoading={setLoading}
                      loading={loading}
                      setShowMarkerAlert={setShowMarkerAlert}
                      positionDevice={positionDevice} />
                  }
                  {showUserLocations &&
                    <UserLocationMarkers propertyId={propertyId} />
                  }
                </FeatureGroup>

                {showHazardZones && (
                  <LayerGroup>
                    {hazardZoneData
                      .filter((h) => h.lat && h.lng && (!h.shape_type || h.shape_type === 'marker'))
                      .map((hazard) => (
                        <Circle
                          key={`zone-hazard-${hazard.id}`}
                          center={[parseFloat(hazard.lat), parseFloat(hazard.lng)]}
                          radius={hazard.hazard_radius ? parseFloat(hazard.hazard_radius) : 10}
                          {...HAZARD_ZONE_STYLE}
                        />
                      ))}
                    {activityHazardZoneData
                      .filter((h) => h.lat !== null && h.lng !== null)
                      .map((hazard) => (
                        <Circle
                          key={`zone-activity-${hazard.isNewHazard ? 'new' : 'old'}-${hazard.id}`}
                          center={[hazard.lat, hazard.lng]}
                          radius={hazard.hazard_radius ? parseFloat(hazard.hazard_radius) : 10}
                          {...HAZARD_ZONE_STYLE}
                        />
                      ))}
                  </LayerGroup>
                )}

                {/*<PrimaryObjectModal
                propertyId={propertyId}
                updatePropertyDetail={updatePropertyDetail}
                editArea={editArea}
                onEdited={onEdited}
                propertyAcreageUnit={propertyAcreageUnit}
            />*/}
              </Map>
            </div>
            :
            <div style={{ height: "100%", width: '100%', position: 'relative' }}>
              {/* Route drawing control bar */}
              {routeDrawing && (
                <DrawingControlBar>
                  <input
                    value={routeLabel}
                    onChange={e => setRouteLabel(e.target.value)}
                    placeholder="Route name…"
                    style={{ padding: '5px 10px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 13, width: 140 }}
                  />
                  <ColorSwatches colors={['#EF4444', '#EAB308', '#3B82F6', '#F97316', '#16A34A', '#6B7280']} value={routeColor} onChange={setRouteColor} />
                  <div style={{ display: 'flex', gap: 3 }}>
                    {['Solid', 'Dashed', 'Dotted'].map(s => {
                      const v = s.toLowerCase(); return (
                        <button key={v} onClick={() => setRouteStyle(v)} style={{ padding: '4px 8px', borderRadius: 5, fontSize: 12, cursor: 'pointer', fontWeight: routeStyle === v ? 600 : 400, background: routeStyle === v ? '#0d3825' : 'white', color: routeStyle === v ? 'white' : '#374151', border: routeStyle === v ? '1px solid #0d3825' : '1px solid #d1d5db' }}>{s}</button>
                      );
                    })}
                  </div>
                  <span style={{ color: '#6b7280', fontSize: 12 }}>
                    {routeWaypoints.length} pt{routeWaypoints.length !== 1 ? 's' : ''}
                  </span>
                  <Button type="primary" size="small" onClick={handleFinishRoute} disabled={routeWaypoints.length < 2}
                    style={{ background: '#0B2A1B', borderColor: '#0B2A1B' }}>
                    Finish Route
                  </Button>
                  <Button size="small" onClick={handleCancelRoute}>Cancel</Button>
                </DrawingControlBar>
              )}
              {/* Zone drawing control bar */}
              {activeTool === 'zone' && (
                <DrawingControlBar>
                  <input
                    value={zoneLabel}
                    onChange={e => setZoneLabel(e.target.value)}
                    placeholder="Zone name…"
                    style={{ padding: '5px 10px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 13, width: 140 }}
                  />
                  <ColorSwatches colors={['#EF4444', '#EAB308', '#16A34A', '#3B82F6', '#F97316', '#8B5CF6', '#6B7280']} value={zoneColor} onChange={setZoneColor} />
                  <div style={{ display: 'flex', gap: 3 }}>
                    {['Solid', 'Dashed', 'Dotted'].map(s => {
                      const v = s.toLowerCase(); return (
                        <button key={v} onClick={() => setZoneStyle(v)} style={{ padding: '4px 8px', borderRadius: 5, fontSize: 12, cursor: 'pointer', fontWeight: zoneStyle === v ? 600 : 400, background: zoneStyle === v ? '#0d3825' : 'white', color: zoneStyle === v ? 'white' : '#374151', border: zoneStyle === v ? '1px solid #0d3825' : '1px solid #d1d5db' }}>{s}</button>
                      );
                    })}
                  </div>
                  <span style={{ color: '#6b7280', fontSize: 12 }}>
                    {zoneWaypoints.length} pt{zoneWaypoints.length !== 1 ? 's' : ''}
                  </span>
                  <Button type="primary" size="small" onClick={handleFinishZone} disabled={zoneWaypoints.length < 3}
                    style={{ background: '#0d3825', borderColor: '#0d3825' }}>
                    Finish Zone
                  </Button>
                  <Button size="small" onClick={handleCancelZone}>Cancel</Button>
                </DrawingControlBar>
              )}
              <AnnotationToolbar
                activeCategory={activeAnnotationCategory}
                pendingType={pendingAnnotationType}
                routeDrawing={routeDrawing}
                drawingPanelOpen={drawingPanelOpen}
                activeTool={activeTool}
                showDrawingTools={showAnnotationToolbar}
                onCategoryClick={(key) => {
                  setActiveAnnotationCategory(prev => prev === key ? null : key);
                  setDrawingPanelOpen(false);
                  setPendingAnnotationType(null);
                }}
                onSelectIcon={(type) => {
                  setPendingAnnotationType(type);
                  setActiveAnnotationCategory(null);
                }}
                onClosePanel={() => {
                  setActiveAnnotationCategory(null);
                  setDrawingPanelOpen(false);
                }}
                onToggleDrawingPanel={() => {
                  setDrawingPanelOpen(prev => !prev);
                  setActiveAnnotationCategory(null);
                  setPendingAnnotationType(null);
                }}
                onSelectDrawingTool={handleSelectDrawingTool}
              />
              <DrawTool
                center={position[0] != null ? position : [-25.3444, 131.0369]}
                zoom={zoomDefault}
                darkTheme={darkTheme}
                primaryObject={primaryObjects}
                layerId={layerChosen}
                setIsEditingLayer={setIsEditingLayer}
                propertyId={propertyId}
                getListLayer={getListLayer}
                setLayerChosen={setLayerChosen}
                isShowAreaLabel={isShowAreaLabel}
                isShowAreaSize={isShowAreaSize}
                notSelectedPrimaryObjectTypeIds={notSelectedPrimaryObjectTypeIds}
                onMapClick={handleMapClick}
                annotationLayers={<>
                  <AnnotationMarkers
                    annotations={showAnnotationToolbar ? annotations : annotations.filter(a => a.annotation_type !== 'text_label')}
                    hiddenCategories={hiddenAnnotationCategories}
                    onDelete={handleAnnotationDelete}
                    onUpdate={handleAnnotationUpdate}
                    eraserMode={activeTool === 'eraser'}
                  />
                  {showAnnotationToolbar && (
                    <AnnotationRouteMarkers
                      routes={annotationRoutes}
                      onDelete={handleRouteDelete}
                      onUpdate={handleRouteUpdate}
                      eraserMode={activeTool === 'eraser'}
                    />
                  )}
                  <ActiveRouteTool
                    isActive={routeDrawing || !!pendingAnnotationType || activeTool === 'zone' || activeTool === 'text' || activeTool === 'eraser'}
                    cursor={activeTool === 'eraser' ? 'cell' : 'crosshair'}
                  />
                  <RouteInProgress waypoints={routeWaypoints} color={routeColor} style={routeStyle} />
                  {showAnnotationToolbar && <ZoneInProgress waypoints={zoneWaypoints} color={zoneColor} style={zoneStyle} />}
                  {showAnnotationToolbar && (
                    <SavedZones
                      zones={annotationRoutes.filter(r => r.type === 'zone')}
                      eraserMode={activeTool === 'eraser'}
                      onEraseZone={(id) => annotationRouteService.destroy(id).then(() => handleRouteDelete(id)).catch(err => console.error('Failed to delete zone:', err))}
                      onUpdate={handleRouteUpdate}
                      onDelete={handleRouteDelete}
                    />
                  )}
                  <Control position="bottomleft">
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 20, marginBottom: 20 }}>
                      <MapLegend
                        annotations={annotations}
                        routes={annotationRoutes}
                        visible={showLegend}
                      />
                      <AnnotationSidePanel
                        hiddenCategories={hiddenAnnotationCategories}
                        onToggleCategory={handleToggleAnnotationCategory}
                        showLegend={showLegend}
                        onToggleLegend={() => setShowLegend(v => !v)}
                        showToolbar={showAnnotationToolbar}
                        onToggleToolbar={() => setShowAnnotationToolbar(v => !v)}
                      />
                    </div>
                  </Control>
                </>}
              />
            </div>
          }
          <NewLiveStockModal propertyId={propertyId} updatePropertyDetail={updatePropertyDetail} propertyPIC={propertyPIC} />
          <AddLayerModal
            propertyId={propertyId}
            listLayer={listLayer}
            setListLayer={setListLayer}
          />
          <ModalActivity
            cancelModalActivity={closeHazardActivityModal}
            visibleModalActivity={hazardActivityModalVisible}
            propertyId={propertyId}
            activityActive={hazardActivityEditingPolicy?.type || 'hazard'}
            searchAssetAndPolicy={refreshHazardActivities}
            editingPolicy={hazardActivityEditingPolicy}
            propertyCenter={hazardModalCenter}
            initialPoint={hazardActivityInitialPoint}
            saveToHazardsTable={!hazardActivityEditingPolicy || hazardActivityEditingPolicy?.isNewHazard === true}
            onRequestDrawArea={handleRequestDrawArea}
          />
          <Modal
            className="hazard-shape-selector"
            title="Select Hazard Area Type"
            visible={showHazardShapeSelector}
            onCancel={() => setShowHazardShapeSelector(false)}
            footer={null}
            width={420}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <Button
                block
                size="large"
                onClick={() => handleHazardShapeSelected('marker')}
                style={{ textAlign: 'left', height: 'auto', padding: '12px 16px' }}
              >
                <div style={{ fontWeight: 600 }}>Pin (Point)</div>
                <div style={{ fontSize: '12px', color: '#888' }}>Place a single marker on the map</div>
              </Button>
              <Button
                block
                size="large"
                onClick={() => handleHazardShapeSelected('polygon')}
                style={{ textAlign: 'left', height: 'auto', padding: '12px 16px' }}
              >
                <div style={{ fontWeight: 600 }}>Polygon (Freeform)</div>
                <div style={{ fontSize: '12px', color: '#888' }}>Click to add vertices, double-click to close the shape</div>
              </Button>
              <Button
                block
                size="large"
                onClick={() => handleHazardShapeSelected('circle')}
                style={{ textAlign: 'left', height: 'auto', padding: '12px 16px' }}
              >
                <div style={{ fontWeight: 600 }}>Circle</div>
                <div style={{ fontSize: '12px', color: '#888' }}>Click center point, then drag to set radius</div>
              </Button>
            </div>
          </Modal>
        </PropertyMapPage>

        <AreaSummaryModal
          propertyId={propertyId}
          visibleSummary={visibleSummary}
          cancelSummaryModal={cancelSummaryModal}
          dataObject={dataObject}
          updatePropertyDetail={updatePropertyDetail}
          primaryObjects={primaryObjects}
          currentPos={currentPos}
          onEdited={onEdited}
          propertyAcreageUnit={propertyAcreageUnit}
        />

        <PrimaryObjectFilterModal
          visiblePrimaryObjectFilterModal={visiblePrimaryObjectFilterModal}
          cancelPrimaryObjectFilterModal={cancelPrimaryObjectFilterModal}
          propertyId={propertyId}
          handleFilterPrimaryObjectType={handleFilterPrimaryObjectType}
          selectedPrimaryObjectTypeIds={selectedPrimaryObjectTypeIds}
          isShowAreaLabel={isShowAreaLabel}
          setIsShowAreaLabel={setIsShowAreaLabel}
          isShowAreaSize={isShowAreaSize}
          setIsShowAreaSize={setIsShowAreaSize}
          isShowEdgeDistance={isShowEdgeDistance}
          setIsShowEdgeDistance={handleChangeShowEdgeDistance}
          canEditMapSettings={hasDrawPermission}
        />
        <HarvestFilterModal
          harvest={harvest}
          visibleBlockFilter={visibleBlockFilter}
          cancelBlockFilterModal={cancelBlockFilterModal}
          propertyId={propertyId}
          handleFilterBlock={handleFilterBlock}
          selectedBlocks={selectedBlocks}
          isShowBlockLabel={isShowBlockLabel}
          setIsShowBlockLabel={setIsShowBlockLabel}
        />
        <AssetFilterModal
          visibleAssetFilterModal={visibleAssetFilterModal}
          selectedAssetCategories={selectedAssetCategories}
          setSelectedAssetCategories={setSelectedAssetCategories}
          cancelAssetFilterModal={cancelAssetFilterModal}
        />

      </LayoutWrapper>

      {/* Text Label input modal */}
      <Modal
        title="Add Text Label"
        open={textLabelModalVisible}
        onOk={handleTextLabelConfirm}
        onCancel={handleTextLabelCancel}
        okText="Add Label"
        okButtonProps={{ disabled: !textLabelInput.trim() }}
      >
        <Input
          placeholder="Enter label text..."
          value={textLabelInput}
          onChange={e => setTextLabelInput(e.target.value)}
          onPressEnter={handleTextLabelConfirm}
          autoFocus
        />
      </Modal>

      {/* Save View Modal */}
      <Modal
        title="Save Current View"
        open={saveViewModalVisible}
        onOk={handleSaveView}
        onCancel={() => { setSaveViewModalVisible(false); setNewViewName(''); }}
        okText="Save"
        okButtonProps={{ disabled: !newViewName.trim(), className: 'btn-success' }}
      >
        <div style={{ marginBottom: 8 }}>View Name</div>
        <Input
          placeholder="Enter view name..."
          value={newViewName}
          onChange={e => setNewViewName(e.target.value)}
          onPressEnter={handleSaveView}
          autoFocus
        />
      </Modal>

      {/* Manage Views Modal */}
      <Modal
        title="Manage Views"
        open={manageViewsModalVisible}
        onCancel={() => setManageViewsModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setManageViewsModalVisible(false)}>Close</Button>
        ]}
        width={500}
      >
        {/* Property Default Section — visible to all, manageable by owner/admin */}
        {hasFullPermission && (
          <>
            <div style={{ fontWeight: 600, marginBottom: 8, color: '#2d6a4f' }}>
              <HomeOutlined style={{ marginRight: 6 }} />Property Default
            </div>
            {propertyDefaultView ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0', marginBottom: 16, borderBottom: '1px solid #f0f0f0' }}>
                <span style={{ flex: 1 }}>{propertyDefaultView.name}</span>
                <Button
                  type="text"
                  size="small"
                  danger
                  title="Remove Property Default"
                  onClick={handleDeletePropertyDefault}
                  icon={<DeleteOutlined />}
                />
              </div>
            ) : (
              <p style={{ color: '#999', marginBottom: 16 }}>No property default set. Use the <HomeOutlined /> icon below to set one.</p>
            )}
          </>
        )}

        {/* User's Own Views */}
        <div style={{ fontWeight: 600, marginBottom: 8 }}>
          <StarOutlined style={{ marginRight: 6 }} />My Views
        </div>
        {mapViews.length === 0 ? (
          <p style={{ color: '#999' }}>No saved views yet. Use "Save Current View" to create one.</p>
        ) : (
          mapViews.map(view => (
            <div key={view.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
              <span style={{ flex: 1 }}>{view.name}</span>
              <Space>
                <Button
                  type="text"
                  size="small"
                  title={view.is_user_default === 1 ? 'My Default' : 'Set as My Default'}
                  onClick={() => handleSetUserDefault(view.id)}
                  icon={view.is_user_default === 1 ? <StarFilled style={{ color: '#d4a017' }} /> : <StarOutlined />}
                />
                {hasFullPermission && (
                  <Button
                    type="text"
                    size="small"
                    title="Set as Property Default"
                    onClick={() => handleSetPropertyDefault(view.id)}
                    icon={<HomeOutlined />}
                  />
                )}
                <Button
                  type="text"
                  size="small"
                  danger
                  title="Delete View"
                  onClick={() => handleDeleteView(view.id)}
                  icon={<DeleteOutlined />}
                />
              </Space>
            </div>
          ))
        )}
      </Modal>
    </>
  );
}

export default PropertyMap;
